import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Download, CheckCircle2, XCircle, Upload, Plus, Loader2, Edit3, Trash2, X, Ticket as TicketIcon, UserPlus, FileSpreadsheet, Mail, ShieldCheck, Camera, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { CouponsTab, CampaignsTab, KycTab, PayoutsTab } from "@/pages/AdminPhase3Tabs";

const EXPORTS = ["events","users","registrations","tickets","transactions","leads","waitlist"];
const LEAD_STATUSES = ["New", "Contacted", "Qualified", "Proposal Sent", "Closed Won", "Closed Lost"];
const STATUS_COLORS = { New: "bg-magenta text-white", "Contacted": "bg-orangex text-white", "Qualified": "bg-skyx text-bg", "Proposal Sent": "bg-purplex text-white", "Closed Won": "bg-lime text-bg", "Closed Lost": "bg-white/10 text-muted" };
const CATEGORIES = ["College Fest","Music Festival","Corporate Event","Technical Festival","Cultural","Fashion Show","Wedding","Open Mic","VIP After Party","House Party","Other"];
const EVENT_TYPES = [["ticketed","Ticketed","Sell paid tickets"],["free","Free RSVP","Free registration"],["invite_only","Invite Only","Manual guest list"]];
const LEAD_TYPES = [
  ["organiser", "Organisers", "bg-lime text-bg"],
  ["college", "Colleges", "bg-skyx text-bg"],
  ["attendee", "Attendees", "bg-orangex text-white"],
  ["artist", "Artists", "bg-magenta text-white"],
  ["sponsor", "Sponsors", "bg-purplex text-white"],
];

function BannerUpload({ value, onChange, testId }) {
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3*1024*1024) return toast.error("Max 3MB");
    const r = new FileReader(); r.onload = () => onChange(r.result); r.readAsDataURL(f);
  };
  return (
    <div className="flex items-center gap-3">
      <label className="cursor-pointer" data-testid={`${testId}-label`}>
        <div className="w-20 h-20 rounded-2xl bg-white/5 border-2 border-dashed border-lime/40 overflow-hidden flex items-center justify-center">
          {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] font-mono text-muted text-center">Upload<br/>Banner</span>}
        </div>
        <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testId}-input`} />
      </label>
      <input placeholder="…or paste banner URL" value={value?.startsWith("data:") ? "" : (value || "")} onChange={(e) => onChange(e.target.value)} className="input-dark !text-xs flex-1" data-testid={`${testId}-url`} />
    </div>
  );
}

// ------- CSV Import for Users (inline) -------
function UserImportInline({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const token = localStorage.getItem("eventom_token");
      const res = await fetch(`${API}/admin/import/users`, { method: "POST", body: fd, credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error("Import failed");
      const data = await res.json(); setResult(data);
      toast.success(`Imported ${data.created} users • Skipped ${data.skipped}`); onDone?.();
    } catch (er) { toast.error(er.message || "Import failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <label className="btn-lime !py-2 !px-4 cursor-pointer" data-testid="users-import-label">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} {busy ? "Importing…" : "Import Users (CSV / Excel)"}
        <input type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="users-import-input" />
      </label>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted">Columns: name*, email*, role, phone, password</div>
      {result && <div className="text-[10px] font-mono uppercase text-lime">✓ {result.created} created · {result.skipped} skipped · {result.errors} errors</div>}
    </div>
  );
}

// ------- Manual Ticket Modal (Pass / Cash) -------
function ManualTicketModal({ events, onClose, onSaved }) {
  const [form, setForm] = useState({
    event_id: events[0]?.id || "", tier_name: "General", price: 0, quantity: 1,
    attendee_name: "", attendee_email: "", attendee_phone: "", payment_method: "pass", notes: ""
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const res = await api.post("/admin/tickets/manual", form);
      toast.success(`Ticket issued • Code: ${res.data.ticket?.ticket_code || "OK"}`);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-bg/80 backdrop-blur-md z-50 flex items-center justify-center p-4" data-testid="manual-ticket-modal">
      <div className="max-w-lg w-full glass rounded-3xl">
        <div className="flex justify-between items-center border-b border-white/10 p-4">
          <div className="font-display text-2xl uppercase">Generate Ticket</div>
          <button onClick={onClose} data-testid="manual-ticket-close" className="icon-circle !w-9 !h-9 bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <select required data-testid="mt-event" value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} className="input-dark">
            <option value="" disabled className="bg-bg">Select event…</option>
            {events.map((e) => <option key={e.id} value={e.id} className="bg-bg">{e.title} — {e.city}</option>)}
          </select>
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="Tier / Category" value={form.tier_name} onChange={(e) => setForm({ ...form, tier_name: e.target.value })} className="input-dark !text-sm" data-testid="mt-tier" />
            <input type="number" min={0} placeholder="Price ₹" value={form.price} onChange={(e) => setForm({ ...form, price: (e.target.value === "" ? "" : Number(e.target.value)) })} className="input-dark !text-sm" data-testid="mt-price" />
            <input type="number" min={1} placeholder="Qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: (e.target.value === "" ? "" : Number(e.target.value)) })} className="input-dark !text-sm" data-testid="mt-qty" />
          </div>
          <input required placeholder="Attendee full name" value={form.attendee_name} onChange={(e) => setForm({ ...form, attendee_name: e.target.value })} className="input-dark" data-testid="mt-name" />
          <div className="grid grid-cols-2 gap-2">
            <input type="email" placeholder="Email (optional)" value={form.attendee_email} onChange={(e) => setForm({ ...form, attendee_email: e.target.value })} className="input-dark" data-testid="mt-email" />
            <input placeholder="Phone (optional)" value={form.attendee_phone} onChange={(e) => setForm({ ...form, attendee_phone: e.target.value })} className="input-dark" data-testid="mt-phone" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[["pass","Complimentary Pass","No payment"],["cash","Cash Received","Payment collected at venue"]].map(([val, label, sub]) => (
              <button type="button" key={val} data-testid={`mt-method-${val}`}
                onClick={() => setForm({ ...form, payment_method: val })}
                className={`text-left rounded-2xl border p-3 ${form.payment_method === val ? "border-lime bg-lime/10" : "border-white/10 bg-white/5"}`}>
                <div className="font-bold uppercase text-xs tracking-wider">{label}</div>
                <div className="text-[10px] font-mono text-muted mt-0.5">{sub}</div>
              </button>
            ))}
          </div>
          <textarea rows={2} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-dark resize-none" />
          <button disabled={saving} type="submit" data-testid="mt-submit" className="btn-lime w-full justify-center">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <TicketIcon className="w-4 h-4" />} {saving ? "Issuing…" : "Issue Ticket + QR"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ------- Lead Import Modal (Excel/CSV per type) -------
function LeadImportModal({ onClose, onDone }) {
  const [type, setType] = useState("organiser");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const token = localStorage.getItem("eventom_token");
      const res = await fetch(`${API}/admin/import/leads?type=${type}`, { method: "POST", body: fd, credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error((await res.json()).detail || "Import failed");
      const data = await res.json();
      setResult(data);
      toast.success(`Imported ${data.created} ${type} leads`);
      onDone?.();
    } catch (er) { toast.error(er.message || "Import failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="fixed inset-0 bg-bg/80 backdrop-blur-md z-50 flex items-center justify-center p-4" data-testid="lead-import-modal">
      <div className="max-w-lg w-full glass rounded-3xl">
        <div className="flex justify-between items-center border-b border-white/10 p-4">
          <div className="font-display text-2xl uppercase">Import Leads</div>
          <button onClick={onClose} data-testid="lead-import-close" className="icon-circle !w-9 !h-9 bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-lime mb-2">Lead type</div>
            <div className="grid grid-cols-5 gap-2">
              {LEAD_TYPES.map(([val, label, color]) => (
                <button key={val} data-testid={`lead-type-${val}`}
                  onClick={() => setType(val)}
                  className={`text-center rounded-2xl border p-2 ${type === val ? "border-lime bg-lime/10" : "border-white/10 bg-white/5"}`}>
                  <div className={`inline-block px-2 py-0.5 rounded-full font-mono text-[9px] uppercase ${color}`}>{label}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-muted">Columns: <span className="text-lime">name*</span>, phone, email, company, city, notes</div>
          <label className="btn-primary w-full justify-center cursor-pointer" data-testid="lead-import-file-label">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {busy ? "Importing…" : `Upload .csv / .xlsx for ${type}`}
            <input type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="lead-import-input" />
          </label>
          {result && (
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="card-lime p-2"><div className="font-display text-3xl">{result.created}</div><div className="text-[10px] font-mono uppercase">Created</div></div>
              <div className="card-dark p-2 border border-line"><div className="font-display text-3xl text-muted">{result.skipped}</div><div className="text-[10px] font-mono uppercase text-muted">Skipped</div></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ------- User Detail Modal (with delete) -------
function UserDetailModal({ user, onClose, onDelete }) {
  return (
    <div className="fixed inset-0 bg-bg/80 backdrop-blur-md z-50 flex items-center justify-center p-4" data-testid="user-detail-modal">
      <div className="max-w-md w-full glass rounded-3xl overflow-hidden">
        <div className="flex justify-between items-center border-b border-white/10 p-4">
          <div className="font-display text-xl uppercase">User Details</div>
          <button onClick={onClose} className="icon-circle !w-9 !h-9 bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-4">
            {user.profile_picture ? <img src={user.profile_picture} alt="" className="w-20 h-20 rounded-2xl object-cover" /> : <div className="w-20 h-20 rounded-2xl bg-pink text-white flex items-center justify-center font-display text-3xl">{user.name?.[0]}</div>}
            <div>
              <div className="font-display text-2xl uppercase leading-tight">{user.name}</div>
              <div className={`inline-block mt-1 px-2 py-0.5 rounded-full font-mono text-[10px] uppercase ${user.role === "admin" ? "bg-pink text-white" : user.role === "organizer" ? "bg-lime text-ink" : "bg-white/10"}`}>{user.role}</div>
            </div>
          </div>
          <dl className="grid grid-cols-3 gap-x-2 gap-y-2 text-sm">
            <dt className="text-muted font-mono uppercase text-[10px]">Email</dt><dd className="col-span-2 break-all">{user.email}</dd>
            <dt className="text-muted font-mono uppercase text-[10px]">Phone</dt><dd className="col-span-2">{user.phone || "—"}</dd>
            <dt className="text-muted font-mono uppercase text-[10px]">Joined</dt><dd className="col-span-2 font-mono text-xs">{new Date(user.created_at).toLocaleString()}</dd>
            <dt className="text-muted font-mono uppercase text-[10px]">User ID</dt><dd className="col-span-2 font-mono text-[10px] break-all text-muted">{user.id}</dd>
          </dl>
          {user.role !== "admin" && (
            <button onClick={() => { if (window.confirm(`Delete ${user.name}? Their favorites and registrations will also be removed.`)) onDelete(user.id); }}
              data-testid={`user-detail-delete-${user.id}`}
              className="btn-ghost w-full justify-center !text-magenta"><Trash2 className="w-4 h-4" /> Delete User</button>
          )}
        </div>
      </div>
    </div>
  );
}

function AddEventCard({ onDone }) {
  const empty = { title: "", description: "", banner: "", category: "College Fest", location: "", city: "", date: "", organizer_name: "", organizer_email: "", tags: "", event_type: "ticketed", type: "event", tiers: [{ name: "Regular", price: 349, quantity: 500 }], status: "approved" };
  const [form, setForm] = useState(empty); const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean), tiers: form.event_type === "ticketed" ? form.tiers.filter((t) => t.name && t.price >= 0) : [] };
      await api.post("/admin/events", payload); toast.success("Event created & approved"); setForm(empty); onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="card-dark p-6 space-y-3">
      <div className="chip bg-pink/15 text-pink">Manual Event Add</div>
      <div className="font-display uppercase text-2xl">Create event as admin</div>
      <input required data-testid="admin-add-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark" />
      <textarea data-testid="admin-add-desc" rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark resize-none" />
      <BannerUpload value={form.banner} onChange={(v) => setForm({ ...form, banner: v })} testId="admin-add-banner" />
      <div className="grid grid-cols-2 gap-2">
        <input required placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-dark" />
        <input required type="date" data-testid="admin-add-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-dark" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-dark">
          {CATEGORIES.map((c) => <option key={c} className="bg-bg">{c}</option>)}
        </select>
        <input placeholder="Organizer email (optional)" value={form.organizer_email} onChange={(e) => setForm({ ...form, organizer_email: e.target.value })} className="input-dark" />
      </div>
      <div className="inline-flex gap-1 p-1 rounded-full bg-surface" data-testid="admin-add-type-toggle">
        {[["event", "Event"], ["experience", "Experience"]].map(([v, l]) => (
          <button type="button" key={v} data-testid={`admin-add-type-${v}`} onClick={() => setForm({ ...form, type: v })}
            className={`px-4 py-1.5 text-xs font-mono uppercase tracking-widest rounded-full transition-colors ${(form.type || "event") === v ? "bg-lime text-ink" : "text-muted hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {EVENT_TYPES.map(([val, label, sub]) => (
          <button type="button" key={val} data-testid={`admin-add-type-${val}`} onClick={() => setForm({ ...form, event_type: val })}
            className={`text-left rounded-2xl border p-2 ${form.event_type === val ? "border-lime bg-lime/10" : "border-white/10 bg-white/5"}`}>
            <div className="font-bold uppercase text-[11px] tracking-wider">{label}</div>
            <div className="text-[9px] font-mono text-muted">{sub}</div>
          </button>
        ))}
      </div>
      {form.event_type === "ticketed" && (
        <div className="grid grid-cols-3 gap-2">
          <input placeholder="Tier name" value={form.tiers[0].name} onChange={(e) => { const arr = [...form.tiers]; arr[0].name = e.target.value; setForm({ ...form, tiers: arr }); }} className="input-dark !text-sm" />
          <input type="number" placeholder="₹ Price" value={form.tiers[0].price} onChange={(e) => { const arr = [...form.tiers]; arr[0].price = (e.target.value === "" ? "" : Number(e.target.value)); setForm({ ...form, tiers: arr }); }} className="input-dark !text-sm" />
          <input type="number" placeholder="Qty" value={form.tiers[0].quantity} onChange={(e) => { const arr = [...form.tiers]; arr[0].quantity = (e.target.value === "" ? "" : Number(e.target.value)); setForm({ ...form, tiers: arr }); }} className="input-dark !text-sm" />
        </div>
      )}
      <button disabled={saving} type="submit" data-testid="admin-add-submit" className="btn-lime w-full justify-center">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {saving ? "Saving..." : "Create + Approve"}</button>
    </form>
  );
}

function EditEventModal({ event, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...event, tags: (event.tags || []).join(", "),
    tiers: event.tiers?.length ? event.tiers : [{ name: "Regular", price: 349, quantity: 500 }],
    event_type: event.event_type || "ticketed", date: (event.date || "").split("T")[0],
    time: event.time || "", end_time: event.end_time || "",
    registration_opens_at: event.registration_opens_at || "",
    registration_closes_at: event.registration_closes_at || "",
    ai_photo_gallery_url: event.ai_photo_gallery_url || "",
    ai_gallery_qr_path: event.ai_gallery_qr_path || "",
    sponsors: event.sponsors || [],
    artists: event.artists || [],
    last_year_media: event.last_year_media || [],
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags, tiers: form.event_type === "ticketed" ? form.tiers.filter((t) => t.name && t.price >= 0) : [] };
      await api.patch(`/events/${event.id}`, payload); toast.success("Event updated"); onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-bg/80 backdrop-blur-md z-50 flex items-center justify-center p-4" data-testid="admin-edit-modal">
      <div className="max-w-2xl w-full glass rounded-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center border-b border-white/10 p-4 sticky top-0 bg-bg/95 z-10">
          <div className="font-display text-2xl uppercase">Edit Event</div>
          <button onClick={onClose} data-testid="admin-edit-close" className="icon-circle !w-9 !h-9 bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark" data-testid="admin-edit-title" />
          <textarea rows={3} placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark resize-none" />
          <BannerUpload value={form.banner} onChange={(v) => setForm({ ...form, banner: v })} testId="admin-edit-banner" />
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="City" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-dark" />
            <input placeholder="Location / Venue" value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} className="input-dark" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-dark">
              {CATEGORIES.map((c) => <option key={c} className="bg-bg">{c}</option>)}
            </select>
            <input required type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-dark" />
          </div>
          <div className="inline-flex gap-1 p-1 rounded-full bg-surface" data-testid="admin-edit-type-toggle">
            {[["event", "Event"], ["experience", "Experience"]].map(([v, l]) => (
              <button type="button" key={v} data-testid={`admin-edit-listing-type-${v}`} onClick={() => setForm({ ...form, type: v })}
                className={`px-4 py-1.5 text-xs font-mono uppercase tracking-widest rounded-full transition-colors ${(form.type || "event") === v ? "bg-lime text-ink" : "text-muted hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Start time</label><input type="time" data-testid="admin-edit-time" value={form.time || ""} onChange={(e) => setForm({ ...form, time: e.target.value })} className="input-dark !mt-1" /></div>
            <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">End time</label><input type="time" data-testid="admin-edit-end-time" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="input-dark !mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Registration opens</label><input type="datetime-local" value={form.registration_opens_at || ""} onChange={(e) => setForm({ ...form, registration_opens_at: e.target.value })} className="input-dark !mt-1 !text-xs" /></div>
            <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Registration closes</label><input type="datetime-local" value={form.registration_closes_at || ""} onChange={(e) => setForm({ ...form, registration_closes_at: e.target.value })} className="input-dark !mt-1 !text-xs" /></div>
          </div>
          <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="input-dark" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-lime">Live "Going" count (social proof pill on cards)</label>
              <input type="number" min={0} data-testid="admin-edit-going-count" placeholder="e.g. 340" value={form.going_count ?? 0} onChange={(e) => setForm({ ...form, going_count: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)) })} className="input-dark !mt-1" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-magenta">"Selling fast — X left" pill (0 = hidden)</label>
              <input type="number" min={0} data-testid="admin-edit-tickets-left" placeholder="e.g. 12" value={form.tickets_left ?? 0} onChange={(e) => setForm({ ...form, tickets_left: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)) })} className="input-dark !mt-1" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-lime">AI Photo Gallery Link (optional)</label>
            <input type="url" data-testid="admin-edit-ai-gallery" placeholder="https://ai.eventom.online/…" value={form.ai_photo_gallery_url || ""} onChange={(e) => setForm({ ...form, ai_photo_gallery_url: e.target.value })} className="input-dark !mt-1" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-lime">Gallery QR image (optional)</label>
            <div className="mt-1 flex items-center gap-3">
              <label className="btn-ghost !py-2 !px-3 !text-xs cursor-pointer" data-testid="admin-edit-qr-upload">
                <Upload className="w-3.5 h-3.5" /> {form.ai_gallery_qr_path ? "Replace QR" : "Upload QR"}
                <input type="file" accept="image/*" hidden onChange={async (ev) => {
                  const f = ev.target.files?.[0]; if (!f) return;
                  if (f.size > 3*1024*1024) return toast.error("Max 3MB");
                  const fd = new FormData(); fd.append("file", f);
                  try {
                    const { data } = await api.post("/uploads?kind=gallery-qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setForm({ ...form, ai_gallery_qr_path: data.path });
                    toast.success("QR uploaded");
                  } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
                }} />
              </label>
              {form.ai_gallery_qr_path && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${form.ai_gallery_qr_path}`} alt="QR" className="w-16 h-16 bg-white p-1 rounded" data-testid="admin-edit-qr-preview" />}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {EVENT_TYPES.map(([val, label, sub]) => (
              <button type="button" key={val} onClick={() => setForm({ ...form, event_type: val })} data-testid={`admin-edit-type-${val}`}
                className={`text-left rounded-2xl border p-2 ${form.event_type === val ? "border-lime bg-lime/10" : "border-white/10 bg-white/5"}`}>
                <div className="font-bold uppercase text-[11px] tracking-wider">{label}</div>
                <div className="text-[9px] font-mono text-muted">{sub}</div>
              </button>
            ))}
          </div>
          {form.event_type === "ticketed" && (
            <>
              <div className="font-mono text-xs uppercase tracking-widest text-lime mt-2">Ticket Tiers</div>
              {form.tiers.map((t, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                  <input placeholder="Tier" value={t.name} onChange={(e) => { const arr = [...form.tiers]; arr[idx].name = e.target.value; setForm({ ...form, tiers: arr }); }} className="input-dark !py-2 !text-sm" />
                  <input type="number" placeholder="₹" value={t.price} onChange={(e) => { const arr = [...form.tiers]; arr[idx].price = (e.target.value === "" ? "" : Number(e.target.value)); setForm({ ...form, tiers: arr }); }} className="input-dark !py-2 !text-sm" />
                  <input type="number" placeholder="Qty" value={t.quantity} onChange={(e) => { const arr = [...form.tiers]; arr[idx].quantity = (e.target.value === "" ? "" : Number(e.target.value)); setForm({ ...form, tiers: arr }); }} className="input-dark !py-2 !text-sm" />
                  <button type="button" onClick={() => setForm({ ...form, tiers: form.tiers.filter((_, i) => i !== idx) })} className="icon-circle !w-8 !h-8 bg-white/5 text-magenta"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button type="button" onClick={() => setForm({ ...form, tiers: [...form.tiers, { name: "", price: 0, quantity: 0 }] })} className="text-xs font-mono uppercase underline text-lime">+ Add tier</button>
            </>
          )}

          <div className="border-t border-white/10 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-xs uppercase tracking-widest text-purple">Sponsors ({(form.sponsors || []).length})</div>
              <button type="button" onClick={() => setForm({ ...form, sponsors: [...(form.sponsors || []), { name: "", logo_url: "", type: "Presenting", website: "" }] })} className="text-[10px] font-mono uppercase underline text-lime" data-testid="add-sponsor">+ Add sponsor</button>
            </div>
            {(form.sponsors || []).map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 mb-1.5" data-testid={`sponsor-row-${i}`}>
                <input placeholder="Sponsor name" value={s.name} onChange={(e) => { const arr = [...form.sponsors]; arr[i].name = e.target.value; setForm({ ...form, sponsors: arr }); }} className="input-dark !py-2 !text-xs" />
                <input placeholder="Logo URL" value={s.logo_url} onChange={(e) => { const arr = [...form.sponsors]; arr[i].logo_url = e.target.value; setForm({ ...form, sponsors: arr }); }} className="input-dark !py-2 !text-xs" />
                <input placeholder="Type (Presenting, Powered by…)" value={s.type} onChange={(e) => { const arr = [...form.sponsors]; arr[i].type = e.target.value; setForm({ ...form, sponsors: arr }); }} className="input-dark !py-2 !text-xs" />
                <button type="button" onClick={() => setForm({ ...form, sponsors: form.sponsors.filter((_, k) => k !== i) })} className="icon-circle !w-8 !h-8 bg-white/5 text-magenta"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-xs uppercase tracking-widest text-skyx">Artists / Lineup ({(form.artists || []).length})</div>
              <button type="button" onClick={() => setForm({ ...form, artists: [...(form.artists || []), { name: "", genre: "", photo: "" }] })} className="text-[10px] font-mono uppercase underline text-lime" data-testid="add-artist">+ Add artist</button>
            </div>
            {(form.artists || []).map((a, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 mb-1.5" data-testid={`artist-row-${i}`}>
                <input placeholder="Artist name" value={a.name} onChange={(e) => { const arr = [...form.artists]; arr[i].name = e.target.value; setForm({ ...form, artists: arr }); }} className="input-dark !py-2 !text-xs" />
                <input placeholder="Genre / role" value={a.genre} onChange={(e) => { const arr = [...form.artists]; arr[i].genre = e.target.value; setForm({ ...form, artists: arr }); }} className="input-dark !py-2 !text-xs" />
                <input placeholder="Photo URL" value={a.photo} onChange={(e) => { const arr = [...form.artists]; arr[i].photo = e.target.value; setForm({ ...form, artists: arr }); }} className="input-dark !py-2 !text-xs" />
                <button type="button" onClick={() => setForm({ ...form, artists: form.artists.filter((_, k) => k !== i) })} className="icon-circle !w-8 !h-8 bg-white/5 text-magenta"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-xs uppercase tracking-widest text-orangex">Last-year photos & videos ({(form.last_year_media || []).length})</div>
              <button type="button" onClick={() => setForm({ ...form, last_year_media: [...(form.last_year_media || []), { url: "", caption: "" }] })} className="text-[10px] font-mono uppercase underline text-lime" data-testid="add-lastyear">+ Add media</button>
            </div>
            <div className="text-[10px] text-muted font-mono mb-1.5">Image URLs, YouTube or Instagram links — will play inline on the event page.</div>
            {(form.last_year_media || []).map((m, i) => (
              <div key={i} className="grid grid-cols-[2fr_1fr_auto] gap-2 mb-1.5" data-testid={`lastyear-row-${i}`}>
                <input placeholder="Image / YouTube / Instagram URL" value={m.url} onChange={(e) => { const arr = [...form.last_year_media]; arr[i].url = e.target.value; setForm({ ...form, last_year_media: arr }); }} className="input-dark !py-2 !text-xs" />
                <input placeholder="Caption" value={m.caption} onChange={(e) => { const arr = [...form.last_year_media]; arr[i].caption = e.target.value; setForm({ ...form, last_year_media: arr }); }} className="input-dark !py-2 !text-xs" />
                <button type="button" onClick={() => setForm({ ...form, last_year_media: form.last_year_media.filter((_, k) => k !== i) })} className="icon-circle !w-8 !h-8 bg-white/5 text-magenta"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>

          <button disabled={saving} type="submit" data-testid="admin-edit-submit" className="btn-primary w-full justify-center">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit3 className="w-4 h-4" />} {saving ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
}

function FreshStartDangerZone() {
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (phrase !== "FRESH START") { toast.error('Type exactly "FRESH START" to confirm'); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/fresh-start", { confirm: phrase });
      toast.success(`Fresh start done — wiped: ${Object.entries(data.wiped || {}).map(([k, v]) => `${k}:${v}`).join(", ") || "nothing to wipe"}`);
      setPhrase("");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-8 rounded-3xl border border-magenta/50 bg-magenta/10 p-6" data-testid="fresh-start-zone">
      <div className="font-mono text-[10px] uppercase tracking-widest text-magenta">⚠ Danger Zone</div>
      <div className="font-display text-2xl mt-1">Fresh Start</div>
      <p className="text-xs text-white/60 mt-1 max-w-xl">Permanently deletes ALL non-admin users, tickets, payments, registrations, referrals, community posts, waitlist and OTPs. Events, coupons and admin accounts are kept. Use once to clear test data before launch. This cannot be undone.</p>
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder='Type "FRESH START"' data-testid="fresh-start-input" className="input-dark !w-56 !py-2 !text-sm" />
        <button onClick={run} disabled={busy || phrase !== "FRESH START"} data-testid="fresh-start-btn"
          className="px-5 py-2 rounded-full bg-magenta text-white font-heading font-bold text-sm disabled:opacity-40 hover:shadow-brutal-pink transition-shadow active:scale-95">
          {busy ? "Wiping…" : "Wipe Test Data"}
        </button>
      </div>
    </div>
  );
}

function GenieAnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/genie/analytics").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  const Table = ({ title, rows, accent }) => (
    <div className="glass rounded-3xl p-5">
      <div className="font-display text-lg mb-3">{title} <span className="text-muted text-sm font-body">({rows?.length || 0})</span></div>
      <div className="space-y-1.5 max-h-[420px] overflow-y-auto no-scrollbar">
        {(!rows || rows.length === 0) && <div className="text-sm text-muted font-mono">No taps tracked yet.</div>}
        {(rows || []).map((r, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5 border border-white/5">
            <span className="font-mono text-white/40 text-xs w-6">#{i + 1}</span>
            <span className="flex-1 text-sm truncate">{r.label}</span>
            <span className={`font-mono font-bold text-sm ${accent}`}>{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div data-testid="admin-genie-tab">
      <div className="mb-4 text-sm font-mono text-muted">Total Genie interactions: <span className="text-lime font-bold">{data?.total_interactions ?? "—"}</span></div>
      <div className="grid lg:grid-cols-3 gap-5">
        <Table title="Top Prompts" rows={data?.top_prompts} accent="text-skyx" />
        <Table title="Top Clicked Events" rows={data?.top_cards} accent="text-lime" />
        <Table title="Top Web Clicks" rows={data?.top_web} accent="text-magenta" />
      </div>
    </div>
  );
}

const OPP_TYPES = [["after_party", "After Party"], ["house_party", "House Party"], ["private_party", "Private Party"], ["trip", "Trip"]];

function ImportEventUsersButton({ eventId }) {
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(true);
  const [batches, setBatches] = useState([]);
  const [sending, setSending] = useState("");

  const loadBatches = () => api.get(`/admin/events/${eventId}/import-batches`).then(({ data }) => setBatches(data || [])).catch(() => {});
  useEffect(() => { loadBatches(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [eventId]);

  const onFile = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f); fd.append("notify", notify ? "true" : "false");
    try {
      const { data } = await api.post(`/admin/events/${eventId}/import-users`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} attendees · ${data.users_created} new users · ${data.skipped} skipped`);
      if (notify) {
        if (!data.email_configured) toast.error("Email delivery is not configured — no welcome emails sent");
        else toast.success(`${data.notified} welcome emails sent`);
      }
      if (data.errors > 0) toast.error(`${data.errors} rows had errors (need name + email columns)`);
      loadBatches();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); ev.target.value = ""; }
  };

  const notifyBatch = async (id) => {
    setSending(id);
    try {
      const { data } = await api.post(`/admin/import-batches/${id}/notify`);
      toast.success(`${data.sent} welcome emails sent${data.failed ? ` · ${data.failed} failed` : ""}`);
      loadBatches();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Could not send emails"); }
    finally { setSending(""); }
  };

  return (
    <div className="inline-flex flex-col items-start gap-1.5">
      <div className="flex items-center gap-2">
        <label className={`text-xs px-3 py-1.5 bg-lime/20 text-lime rounded-full font-bold uppercase cursor-pointer inline-flex items-center gap-1 ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={`admin-import-users-${eventId}`} title="Import attendees from AI gallery CSV/Excel (name, email, phone)">
          <FileSpreadsheet className="w-3 h-3" /> {busy ? "Importing…" : "Import CSV"}
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} disabled={busy} data-testid={`admin-import-users-input-${eventId}`} />
        </label>
        <label className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted cursor-pointer">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid={`admin-import-notify-${eventId}`} className="accent-skyx w-3.5 h-3.5" />
          Notify by email
        </label>
      </div>
      {batches.length > 0 && (
        <div className="flex flex-col gap-1" data-testid={`admin-import-batches-${eventId}`}>
          {batches.slice(0, 3).map((b) => (
            <div key={b.id} className="flex items-center gap-2 text-[10px] font-mono text-muted" data-testid={`admin-import-batch-${b.id}`}>
              <span>{(b.rows || []).length} rows · {b.notified ? `${b.notified} mailed` : "not mailed"}</span>
              <button onClick={() => notifyBatch(b.id)} disabled={!!sending} data-testid={`admin-batch-notify-${b.id}`}
                className="px-2 py-0.5 rounded-full bg-skyx/15 text-skyx font-bold uppercase tracking-widest hover:bg-skyx/25 disabled:opacity-50">
                <Mail className="w-2.5 h-2.5 inline mr-1" />{sending === b.id ? "Sending…" : "Send welcome emails"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OpportunitiesAdmin() {
  const blank = { name: "", type: "after_party", date: "", city: "", description: "", images: "", videos: "" };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { api.get("/opportunities/public").then(({ data }) => setItems(data || [])).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const toArr = (s) => String(s || "").split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { ...form, images: toArr(form.images), videos: toArr(form.videos) };
    try {
      if (editing) {
        await api.patch(`/admin/opportunities/${editing}`, payload);
        toast.success("Listing updated");
      } else {
        await api.post("/admin/opportunities", payload);
        toast.success("Listing live on VIP page");
      }
      setForm(blank); setEditing(null); load();
    } catch { toast.error("Failed to save listing"); }
    finally { setBusy(false); }
  };
  const edit = (o) => {
    setEditing(o.id);
    setForm({ name: o.name || "", type: o.type || "after_party", date: o.date || "", city: o.city || "",
      description: o.description || "", images: (o.images || []).join("\n"), videos: (o.videos || []).join("\n") });
    window.scrollTo({ top: 200, behavior: "smooth" });
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this listing?")) return;
    try { await api.delete(`/admin/opportunities/${id}`); toast.success("Removed"); load(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="glass rounded-3xl p-5 mb-6" data-testid="admin-opportunities">
      <div className="font-display text-3xl">VIP After Parties & Trips <span className="text-muted text-sm font-body">({items.length} live)</span></div>
      <div className="text-xs text-muted mt-1 mb-4">Add posters and vibe videos so people can see what the night looks like. Requests and waiting-list entries appear below.</div>
      <form onSubmit={create} className="grid md:grid-cols-2 gap-3" data-testid="admin-opp-form">
        <input required data-testid="admin-opp-name" placeholder="Listing name (e.g. Afterhours @ Rooftop 21)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" />
        <select data-testid="admin-opp-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-dark">
          {OPP_TYPES.map(([k, l]) => <option key={k} value={k} className="bg-bg">{l}</option>)}
        </select>
        <input required type="date" data-testid="admin-opp-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-dark" />
        <input required data-testid="admin-opp-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-dark" />
        <textarea required rows={2} data-testid="admin-opp-desc" placeholder="Description — venue vibe, dress code, entry rules" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark resize-none md:col-span-2" />
        <textarea rows={2} data-testid="admin-opp-images" placeholder="Poster / photo URLs — one per line" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className="input-dark resize-none !text-xs" />
        <textarea rows={2} data-testid="admin-opp-videos" placeholder="Video links (YouTube / Instagram / mp4) — one per line" value={form.videos} onChange={(e) => setForm({ ...form, videos: e.target.value })} className="input-dark resize-none !text-xs" />
        <div className="md:col-span-2 flex gap-2">
          <button disabled={busy} data-testid="admin-opp-submit" className="btn-primary justify-center flex-1">{editing ? "Save changes" : "+ Publish Listing"}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(blank); }} className="btn-ghost" data-testid="admin-opp-cancel">Cancel</button>}
        </div>
      </form>
      {items.length > 0 && (
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {items.map((o) => (
            <div key={o.id} className="rounded-2xl border border-white/10 bg-white/5 p-3 flex items-start justify-between gap-2" data-testid={`admin-opp-${o.id}`}>
              <div className="min-w-0">
                <div className="font-heading font-bold text-sm truncate">{o.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-0.5">{String(o.type || "").replace(/_/g, " ")} · {o.date} · {o.city}</div>
                <div className="text-[10px] font-mono text-skyx mt-1">{(o.images || []).length} photos · {(o.videos || []).length} videos</div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => edit(o)} data-testid={`admin-opp-edit-${o.id}`} className="px-2.5 py-1 rounded-full border border-lime/60 text-lime font-mono text-[9px] font-bold uppercase">Edit</button>
                <button onClick={() => remove(o.id)} data-testid={`admin-opp-delete-${o.id}`} className="px-2.5 py-1 rounded-full border border-magenta/60 text-magenta font-mono text-[9px] font-bold uppercase">Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <VipRequestsPanel />
    </div>
  );
}

function VipRequestsPanel() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/opportunity-requests").then(({ data }) => setRows(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/opportunity-requests/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="mt-6 pt-5 border-t border-white/10" data-testid="admin-vip-requests">
      <div className="font-display text-2xl">VIP requests & waiting list <span className="text-muted text-sm font-body">({rows.length})</span></div>
      {rows.length === 0 ? <div className="text-sm text-muted font-mono mt-2">No requests yet.</div> : (
        <div className="mt-3 grid md:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto no-scrollbar">
          {rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-white/10 bg-white/5 p-3" data-testid={`vip-request-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{r.user_name || r.user_email}</div>
                  <div className="text-[10px] font-mono text-muted truncate">{r.user_phone || "—"} · {r.user_email || "—"}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full font-mono text-[9px] uppercase shrink-0 ${r.status === "approved" || r.status === "confirmed" ? "bg-lime text-ink" : r.status === "rejected" ? "bg-magenta text-white" : "bg-white/10 text-white/70"}`}>{r.status}</span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-skyx mt-1 truncate">{r.opp_name}{r.opp_meta ? ` · ${r.opp_meta}` : ""}</div>
              {r.note && <div className="text-xs text-white/65 mt-1">{r.note}</div>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button onClick={() => setStatus(r.id, "confirmed")} data-testid={`vip-req-confirm-${r.id}`} className="px-2.5 py-1 rounded-full bg-lime text-ink font-mono text-[9px] font-bold uppercase">Confirm</button>
                <button onClick={() => setStatus(r.id, "rejected")} data-testid={`vip-req-reject-${r.id}`} className="px-2.5 py-1 rounded-full bg-magenta text-white font-mono text-[9px] font-bold uppercase">Reject</button>
                {r.user_phone && (
                  <a href={`https://wa.me/91${String(r.user_phone).replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(`Hi ${r.user_name || ""}, about ${r.opp_name} on Eventom —`)}`} target="_blank" rel="noreferrer"
                    data-testid={`vip-req-wa-${r.id}`} className="px-2.5 py-1 rounded-full border border-lime/50 text-lime font-mono text-[9px] font-bold uppercase">WhatsApp</a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PartnerEditorPanel({ eco = [], onDone }) {
  const blank = { name: "", type: "artist", city: "", services: "", portfolio_or_instagram: "", contact_mobile: "", logo_url: "", followers: "", media_links: "" };
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState("");
  const [busy, setBusy] = useState(false);
  const toArr = (s) => String(s || "").split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

  const pick = (id) => {
    setEditId(id);
    const l = eco.find((x) => x.id === id);
    if (!l) return setForm(blank);
    setForm({ name: l.name || "", type: l.type || "artist", city: l.city || "", services: l.services || "",
      portfolio_or_instagram: l.portfolio_or_instagram || "", contact_mobile: l.contact_mobile || "",
      logo_url: l.logo_url || "", followers: l.followers || "", media_links: (l.media_links || []).join("\n") });
  };

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = { ...form, media_links: toArr(form.media_links) };
    try {
      if (editId) { await api.put(`/admin/ecosystem/leads/${editId}`, payload); toast.success("Listing updated"); }
      else { await api.post("/admin/ecosystem/partners", payload); toast.success("Partner added and listed"); }
      setForm(blank); setEditId(""); onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="glass rounded-3xl p-5 mb-5 grid md:grid-cols-2 gap-3" data-testid="admin-partner-editor">
      <div className="md:col-span-2">
        <div className="font-display text-3xl">{editId ? "Edit partner listing" : "Add a partner"}</div>
        <div className="text-xs text-muted mt-1">Added partners go live immediately. Pick an existing listing to edit it.</div>
      </div>
      <select value={editId} onChange={(e) => pick(e.target.value)} className="input-dark md:col-span-2 !text-xs" data-testid="admin-partner-select">
        <option value="">+ New partner…</option>
        {eco.map((l) => <option key={l.id} value={l.id} className="bg-bg">{l.name} · {l.type} · {l.status || "new"}</option>)}
      </select>
      <input required data-testid="admin-partner-name" placeholder="Name / stage name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" />
      <select data-testid="admin-partner-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-dark">
        {["artist","creator","vendor","photographer","freelancer","venue","agency"].map((t) => <option key={t} value={t} className="bg-bg">{t}</option>)}
      </select>
      <input required data-testid="admin-partner-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-dark" />
      <input required data-testid="admin-partner-mobile" placeholder="Contact mobile" value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} className="input-dark" />
      <textarea required rows={2} data-testid="admin-partner-services" placeholder="Services offered" value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} className="input-dark resize-none md:col-span-2" />
      <input data-testid="admin-partner-logo" placeholder="Logo / profile picture URL" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="input-dark" />
      <input data-testid="admin-partner-followers" placeholder="Followers (e.g. 45K) — artists & creators" value={form.followers} onChange={(e) => setForm({ ...form, followers: e.target.value })} className="input-dark" />
      <input data-testid="admin-partner-portfolio" placeholder="Portfolio URL or @instagram" value={form.portfolio_or_instagram} onChange={(e) => setForm({ ...form, portfolio_or_instagram: e.target.value })} className="input-dark md:col-span-2" />
      <textarea rows={2} data-testid="admin-partner-media" placeholder="Showreel links — one per line (images, YouTube, Instagram)" value={form.media_links} onChange={(e) => setForm({ ...form, media_links: e.target.value })} className="input-dark resize-none !text-xs md:col-span-2" />
      <div className="md:col-span-2 flex gap-2">
        <button disabled={busy} className="btn-lime justify-center flex-1" data-testid="admin-partner-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} {editId ? "Save changes" : "Add & list partner"}
        </button>
        {editId && <button type="button" onClick={() => { setEditId(""); setForm(blank); }} className="btn-ghost" data-testid="admin-partner-cancel">Cancel</button>}
      </div>
    </form>
  );
}

function ContestCoverUpload({ value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Max 3MB");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads?kind=contest-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`);
      toast.success("Cover uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-20 h-20 rounded-2xl bg-white/5 border border-white/15 overflow-hidden flex items-center justify-center shrink-0">
        {value ? <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testId}-preview`} /> : <Camera className="w-4 h-4 text-muted" />}
      </div>
      <div className="flex-1 min-w-0">
        <label className="btn-ghost !py-2 !px-3 !text-xs cursor-pointer" data-testid={`${testId}-upload`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload cover
          <input type="file" accept="image/*" hidden onChange={pick} />
        </label>
        <input placeholder="…or paste image URL" value={value?.startsWith("http") ? value : ""} onChange={(e) => onChange(e.target.value)} className="input-dark !text-xs !py-1.5 mt-1.5" data-testid={`${testId}-url`} />
      </div>
    </div>
  );
}

const CTA_KINDS = [
  ["instagram_follow", "Instagram — Follow", "Instagram profile URL"],
  ["instagram_community", "Instagram — Community / Channel", "Instagram channel URL"],
  ["whatsapp_community", "WhatsApp — Community", "WhatsApp community URL"],
  ["form", "Form / Submission", "Google Form / Tally URL"],
  ["link", "Custom link", "Any external URL"],
];

function ContestsAdminTab() {
  const blank = { name: "", description: "", prize: "", end_date: "", mechanic: "instagram_follow", cover_url: "", instagram_handle: "eventom.online", ctas: [] };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState("");
  const [busy, setBusy] = useState(false);
  const [entries, setEntries] = useState({});
  const load = () => api.get("/admin/contests").then(({ data }) => setItems(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const addCta = () => setForm({ ...form, ctas: [...(form.ctas || []), { kind: "instagram_follow", label: "Follow us on Instagram", url: "" }] });
  const updCta = (idx, patch) => setForm({ ...form, ctas: form.ctas.map((c, i) => i === idx ? { ...c, ...patch } : c) });
  const rmCta = (idx) => setForm({ ...form, ctas: form.ctas.filter((_, i) => i !== idx) });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form };
      if (editing) {
        await api.patch(`/admin/contests/${editing}`, payload);
        toast.success("Contest updated");
      } else {
        await api.post("/admin/contests", payload);
        toast.success("Contest live on /contests");
      }
      setForm(blank); setEditing(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const edit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name || "", description: c.description || "", prize: c.prize || "",
      end_date: c.end_date || "", mechanic: c.mechanic || "instagram_follow",
      cover_url: c.cover_url || c.image_path || "",
      instagram_handle: c.instagram_handle || "eventom.online",
      ctas: c.ctas || [],
    });
    window.scrollTo({ top: 200, behavior: "smooth" });
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this contest and all its entries? This can't be undone.")) return;
    try { await api.delete(`/admin/contests/${id}`); toast.success("Contest removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const showEntries = async (id) => {
    try { const { data } = await api.get(`/admin/contests/${id}/entries`); setEntries({ ...entries, [id]: data || [] }); }
    catch { toast.error("Failed to load entries"); }
  };
  const close = async (id) => {
    const ids = window.prompt("Winner user IDs (comma separated, blank = close with no winners)") || "";
    try {
      await api.post(`/admin/contests/${id}/close`, { winner_user_ids: ids.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Contest closed"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="admin-contests-tab">
      <form onSubmit={submit} className="glass p-5 space-y-3">
        <div className="font-display text-3xl">{editing ? "Edit contest" : "Create a contest"}</div>
        <p className="text-xs text-muted">Live instantly on the public Contests page. Add multiple CTAs so entrants can follow, join and share.</p>
        <input required data-testid="contest-name" placeholder="Contest name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" />
        <textarea required rows={2} data-testid="contest-desc" placeholder="What people have to do" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark resize-none" />
        <input required data-testid="contest-prize" placeholder="Prize (e.g. 2 VIP passes)" value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} className="input-dark" />
        <div className="grid grid-cols-2 gap-3">
          <input required type="date" data-testid="contest-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input-dark" />
          <select data-testid="contest-mechanic" value={form.mechanic} onChange={(e) => setForm({ ...form, mechanic: e.target.value })} className="input-dark">
            <option value="instagram_follow" className="bg-bg">Instagram follow</option>
            <option value="share_event" className="bg-bg">Share an event</option>
            <option value="custom" className="bg-bg">Custom (see CTAs)</option>
          </select>
        </div>
        <input data-testid="contest-ig-handle" placeholder="Instagram handle (without @) for the follow CTA — e.g. eventom.online" value={form.instagram_handle} onChange={(e) => setForm({ ...form, instagram_handle: e.target.value.replace(/^@/, "") })} className="input-dark" />
        <ContestCoverUpload value={form.cover_url} onChange={(v) => setForm({ ...form, cover_url: v })} testId="contest-cover" />
        <div className="border-t border-white/10 pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-lime">Contest CTAs (buttons on the card)</div>
            <button type="button" onClick={addCta} data-testid="contest-add-cta" className="text-[10px] font-mono uppercase text-lime underline">+ Add CTA</button>
          </div>
          {(form.ctas || []).length === 0 && <div className="text-[10px] text-muted font-mono">No custom CTAs — the mechanic default will show.</div>}
          {(form.ctas || []).map((c, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-2 space-y-1.5 mb-2" data-testid={`contest-cta-${i}`}>
              <select value={c.kind} onChange={(e) => updCta(i, { kind: e.target.value })} className="input-dark !py-1.5 !text-xs" data-testid={`contest-cta-kind-${i}`}>
                {CTA_KINDS.map(([k, l]) => <option key={k} value={k} className="bg-bg">{l}</option>)}
              </select>
              <input placeholder="Button label" value={c.label} onChange={(e) => updCta(i, { label: e.target.value })} className="input-dark !py-1.5 !text-xs" data-testid={`contest-cta-label-${i}`} />
              <input placeholder={CTA_KINDS.find(([k]) => k === c.kind)?.[2] || "URL"} value={c.url} onChange={(e) => updCta(i, { url: e.target.value })} className="input-dark !py-1.5 !text-xs" data-testid={`contest-cta-url-${i}`} />
              <button type="button" onClick={() => rmCta(i)} className="text-[10px] font-mono uppercase text-magenta underline" data-testid={`contest-cta-rm-${i}`}>Remove</button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button disabled={busy} className="btn-lime justify-center flex-1" data-testid="contest-create">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {editing ? "Save changes" : "Publish contest"}
          </button>
          {editing && <button type="button" onClick={() => { setEditing(""); setForm(blank); }} className="btn-ghost" data-testid="contest-cancel">Cancel</button>}
        </div>
      </form>

      <div className="glass p-5">
        <div className="font-display text-3xl mb-3">Contests ({items.length})</div>
        {items.length === 0 ? <div className="text-sm text-muted font-mono">No contests yet.</div> : (
          <div className="grid gap-3 max-h-[660px] overflow-y-auto no-scrollbar">
            {items.map((c) => (
              <div key={c.id} className="glass !rounded-xl p-3" data-testid={`contest-row-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-sm truncate">{c.name}</div>
                    <div className="text-[10px] font-mono uppercase text-muted">{c.prize} · ends {c.end_date} · {c.entry_count ?? 0} entries</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full font-mono text-[9px] uppercase shrink-0 ${c.status === "active" ? "bg-lime text-ink" : "bg-white/10 text-white/70"}`}>{c.status}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <button onClick={() => edit(c)} data-testid={`contest-edit-${c.id}`} className="btn-ghost !py-1.5 !px-2.5 !text-[10px]"><Edit3 className="w-3 h-3" /> Edit</button>
                  <button onClick={() => showEntries(c.id)} data-testid={`contest-entries-${c.id}`} className="btn-ghost !py-1.5 !px-2.5 !text-[10px]">View entries</button>
                  {c.status === "active" && <button onClick={() => close(c.id)} data-testid={`contest-close-${c.id}`} className="btn-pink !py-1.5 !px-2.5 !text-[10px]">Pick winners & close</button>}
                  <button onClick={() => remove(c.id)} data-testid={`contest-delete-${c.id}`} className="btn-ghost !py-1.5 !px-2.5 !text-[10px] !text-magenta"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
                {entries[c.id] && (
                  <div className="mt-2 text-[10px] font-mono text-muted grid gap-0.5 max-h-32 overflow-auto" data-testid={`contest-entry-list-${c.id}`}>
                    {entries[c.id].length === 0 ? <div>No entries yet.</div> : entries[c.id].map((en) => (
                      <div key={en.id} className="truncate">{en.user_name || en.user_email} · {en.user_id}</div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PartnerCsvImport({ onImported }) {
  const [preview, setPreview] = React.useState(null);
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const upload = async (commit) => {
    if (!file) return toast.error("Pick a CSV first");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/ecosystem/partners/import-csv?commit=${commit}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (commit) {
        toast.success(`Imported ${data.created} partners · ${data.skipped} skipped`);
        onImported && onImported();
      } else {
        toast(`Preview: ${data.eligible} eligible · ${data.skipped} skipped (dry run — nothing saved)`);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = "name,type,city,services,contact_mobile,portfolio_or_instagram,logo_url,followers\n"
              + "Reyansh DJ,dj,Patna,Weddings & nightclub sets,+919999900001,@reyansh_dj,,25000\n"
              + "FrameStory,photographer,Kolkata,Event & candid photography,+919999900002,https://framestory.in,,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "eventom-partners-template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="glass p-5 rounded-3xl" data-testid="partners-csv-import">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="chip !text-lime !border-lime/40 mb-1">Bulk import</div>
          <div className="font-display text-2xl">Add partners from a CSV.</div>
          <div className="text-xs text-muted mt-1">Columns: <span className="font-mono">name, type, city, services, contact_mobile</span> (required) + <span className="font-mono">portfolio_or_instagram, logo_url, followers</span> (optional).</div>
        </div>
        <button onClick={downloadTemplate} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="partners-csv-template"><Download className="w-3.5 h-3.5" /> Download template</button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="btn-ghost !py-2 !px-3 !text-xs cursor-pointer" data-testid="partners-csv-pick">
          <Upload className="w-3.5 h-3.5" /> {file ? file.name : "Pick CSV"}
          <input type="file" accept=".csv,text/csv" hidden onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
        </label>
        <button onClick={() => upload(false)} disabled={!file || busy} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="partners-csv-preview">Preview</button>
        <button onClick={() => upload(true)} disabled={!file || busy} className="btn-lime !py-2 !px-3 !text-xs" data-testid="partners-csv-commit">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Import now
        </button>
      </div>
      {preview && (
        <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="partners-csv-result">
          <div className="px-3 py-2 border-b border-white/10 flex flex-wrap gap-3 text-[11px] font-mono uppercase tracking-widest">
            <span className="text-lime">Eligible: {preview.eligible}</span>
            <span className="text-magenta">Skipped: {preview.skipped}</span>
            <span className="text-muted">Committed: {preview.committed ? "yes" : "no (dry run)"}</span>
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            {preview.preview.map((r, i) => (
              <div key={i} className="px-3 py-2 border-b border-white/5 last:border-b-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-heading font-bold truncate">Row {r.row_index} · {r.name || <span className="text-muted italic">no name</span>}</div>
                  <div className={`text-[10px] font-mono ${r.ok ? "text-lime" : "text-magenta"}`}>{r.reason}</div>
                </div>
                <span className={`chip !text-[9px] ${r.ok ? "!text-lime !border-lime/40" : "!text-magenta !border-magenta/40"}`}>{r.ok ? "OK" : "SKIP"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function PartnersAdminTab() {
  const [eco, setEco] = useState([]);
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState(null); // Iter 49 — admin edit
  const load = () => {
    api.get("/admin/ecosystem/leads").then(({ data }) => setEco(data || [])).catch(() => {});
    api.get("/admin/brands/leads").then(({ data }) => setBrands(data || [])).catch(() => {});
  };
  useEffect(load, []);
  const decide = async (kind, id, status) => {
    try { await api.patch(`/admin/${kind}/leads/${id}`, { status }); toast.success(status === "approved" ? "Approved — now live on the public directory" : "Rejected"); load(); }
    catch { toast.error("Failed"); }
  };
  const removePartner = async (id) => {
    if (!window.confirm("Delete this partner? This cannot be undone.")) return;
    try { await api.delete(`/admin/ecosystem/partners/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const StatusPill = ({ s }) => (
    <span className={`px-2 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest shrink-0 ${s === "approved" ? "bg-lime text-ink" : s === "rejected" ? "bg-magenta text-white" : "bg-white/10 text-white/70"}`}>{s || "new"}</span>
  );
  const Actions = ({ kind, item }) => (
    <div className="mt-2 flex gap-2 flex-wrap">
      {(item.status || "new") !== "approved" && <button onClick={() => decide(kind, item.id, "approved")} data-testid={`partner-approve-${item.id}`} className="px-3 py-1 rounded-full bg-lime text-ink font-mono text-[10px] font-bold uppercase">Approve & List</button>}
      {(item.status || "new") !== "rejected" && <button onClick={() => decide(kind, item.id, "rejected")} data-testid={`partner-reject-${item.id}`} className="px-3 py-1 rounded-full bg-magenta text-white font-mono text-[10px] font-bold uppercase">Reject</button>}
      {kind === "ecosystem" && (
        <>
          <button onClick={() => setEditing(item)} data-testid={`partner-edit-${item.id}`} className="px-3 py-1 rounded-full bg-white/10 text-white font-mono text-[10px] font-bold uppercase hover:bg-white/20">Edit</button>
          <button onClick={() => removePartner(item.id)} data-testid={`partner-delete-${item.id}`} className="px-3 py-1 rounded-full bg-magenta/15 text-magenta font-mono text-[10px] font-bold uppercase hover:bg-magenta hover:text-white">Delete</button>
        </>
      )}
    </div>
  );
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-partners-tab">
      <div className="lg:col-span-2"><PartnerCsvImport onImported={load} /></div>
      <div className="lg:col-span-2"><PartnerEditorPanel eco={eco} onDone={load} /></div>
      <div className="glass rounded-3xl p-5">
        <div className="font-display text-lg mb-1">Ecosystem Listings <span className="text-muted text-sm font-body">({eco.length})</span></div>
        <div className="text-xs text-muted mb-3">Approve to publish on the public Ecosystem directory.</div>
        <div className="space-y-2 max-h-[560px] overflow-y-auto no-scrollbar">
          {eco.length === 0 && <div className="text-sm text-muted font-mono">No submissions yet.</div>}
          {eco.map((l) => (
            <div key={l.id} className="rounded-2xl border border-white/10 bg-white/5 p-3" data-testid={`eco-lead-${l.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{l.name} <span className="text-muted font-body text-xs">· {l.type}</span></div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted">{l.city} · {l.contact_mobile} · {l.created_at?.slice(0, 10)}</div>
                </div>
                <StatusPill s={l.status} />
              </div>
              <div className="text-xs text-white/60 mt-1.5 line-clamp-2">{l.services}</div>
              {l.portfolio_or_instagram && <div className="text-[11px] text-skyx mt-1 truncate">{l.portfolio_or_instagram}</div>}
              <Actions kind="ecosystem" item={l} />
            </div>
          ))}
        </div>
      </div>
      <div className="glass rounded-3xl p-5">
        <div className="font-display text-lg mb-1">Brand Enquiries <span className="text-muted text-sm font-body">({brands.length})</span></div>
        <div className="text-xs text-muted mb-3">Approve to feature on the "Brands on Eventom" wall.</div>
        <div className="space-y-2 max-h-[560px] overflow-y-auto no-scrollbar">
          {brands.length === 0 && <div className="text-sm text-muted font-mono">No enquiries yet.</div>}
          {brands.map((b) => (
            <div key={b.id} className="rounded-2xl border border-white/10 bg-white/5 p-3" data-testid={`brand-lead-${b.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{b.brand_name} <span className="text-muted font-body text-xs">· {b.industry}</span></div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted">{b.contact_email} · {b.contact_mobile}</div>
                </div>
                <StatusPill s={b.status} />
              </div>
              <div className="text-xs text-white/60 mt-1.5">Budget: {b.campaign_budget}{b.target_cities ? ` · ${b.target_cities}` : ""}</div>
              <Actions kind="brands" item={b} />
            </div>
          ))}
        </div>
      </div>
      {editing && <PartnerEditModal partner={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// Iter 49 — Admin edit modal for an ecosystem partner
function PartnerEditModal({ partner, onClose, onSaved }) {
  const [form, setForm] = useState({ ...partner });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      ["name", "category", "city", "bio", "logo_url", "cover_url", "instagram", "website", "email", "phone", "whatsapp", "followers", "rate_card", "status", "featured", "tags"].forEach((k) => {
        if (form[k] !== undefined) payload[k] = form[k];
      });
      await api.patch(`/admin/ecosystem/partners/${partner.id}`, payload);
      toast.success("Partner updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="partner-edit-modal">
      <div className="max-w-2xl w-full rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="themed-pink sticker-wall p-5 relative">
          <div className="relative z-10">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/80">Edit partner</div>
            <div className="font-fancy uppercase text-white leading-[0.9] text-3xl mt-1" style={{ textShadow: "2px 2px 0 rgba(0,0,0,0.3)" }}>
              {form.name}
            </div>
          </div>
        </div>
        <div className="bg-[#0A0A0F] p-5 space-y-3">
          {[
            ["name", "Name"], ["category", "Category"], ["city", "City"],
            ["email", "Email"], ["phone", "Phone"], ["whatsapp", "WhatsApp"],
            ["instagram", "Instagram"], ["website", "Website"],
            ["logo_url", "Logo URL"], ["cover_url", "Cover URL"],
            ["followers", "Followers"], ["rate_card", "Rate card"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] font-mono uppercase tracking-widest text-white/60">{label}</label>
              <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="input-dark !mt-1" data-testid={`partner-edit-${k}`} />
            </div>
          ))}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-white/60">Bio</label>
            <textarea rows={3} value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="input-dark !mt-1" data-testid="partner-edit-bio" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={onClose} data-testid="partner-edit-cancel" className="btn-ghost !py-2 !px-4 !text-xs">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="partner-edit-save" className="btn-pink !py-2 !px-4 !text-xs">{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GateStaffTab({ events = [] }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", event_ids: [] });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/checkers").then(({ data }) => setRows(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleEvent = (id) => setForm((f) => ({ ...f, event_ids: f.event_ids.includes(id) ? f.event_ids.filter((x) => x !== id) : [...f.event_ids, id] }));

  const create = async (e) => {
    e.preventDefault();
    if (form.event_ids.length === 0) return toast.error("Allocate at least one event");
    setBusy(true);
    try {
      await api.post("/admin/checkers", form);
      toast.success("Ticket checker created — they can scan only these events");
      setForm({ name: "", email: "", password: "", event_ids: [] });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const setEvents = async (id, ids) => {
    try { await api.patch(`/admin/checkers/${id}`, { event_ids: ids }); toast.success("Events updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this ticket checker?")) return;
    try { await api.delete(`/admin/checkers/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="admin-gatestaff-tab">
      <form onSubmit={create} className="glass p-5 space-y-3">
        <div className="font-display text-3xl">Ticket checker access</div>
        <p className="text-xs text-muted">They can open ONLY the scanner, and only for the events you tick below.</p>
        <input required data-testid="checker-name" placeholder="Staff name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" />
        <input required type="email" data-testid="checker-email" placeholder="Login email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" />
        <input required data-testid="checker-password" placeholder="Password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dark" />
        <div className="max-h-56 overflow-auto grid gap-1.5 pr-1" data-testid="checker-event-list">
          {events.map((ev) => (
            <label key={ev.id} className="flex items-center gap-2 text-xs text-text/85 cursor-pointer">
              <input type="checkbox" checked={form.event_ids.includes(ev.id)} onChange={() => toggleEvent(ev.id)} data-testid={`checker-event-${ev.id}`} className="accent-lime" />
              <span className="truncate">{ev.title}</span>
              <span className="text-[10px] font-mono text-muted ml-auto shrink-0">{ev.city}</span>
            </label>
          ))}
        </div>
        <button disabled={busy} className="btn-lime w-full justify-center" data-testid="checker-create">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Create checker login
        </button>
      </form>

      <div className="glass p-5">
        <div className="font-display text-3xl mb-3">Gate staff ({rows.length})</div>
        {rows.length === 0 ? <div className="text-sm text-muted">No ticket checkers yet.</div> : (
          <div className="grid gap-3">
            {rows.map((r) => (
              <div key={r.id} className="glass !rounded-xl p-3" data-testid={`checker-row-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-sm truncate">{r.name}</div>
                    <div className="text-[10px] font-mono text-muted truncate">{r.email}</div>
                  </div>
                  <button onClick={() => remove(r.id)} data-testid={`checker-delete-${r.id}`} className="btn-ghost !py-1.5 !px-2.5 !text-[10px]">Remove</button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(r.events || []).map((ev) => (
                    <span key={ev.id} className="chip !text-[9px]">{ev.title}
                      <button onClick={() => setEvents(r.id, (r.allowed_event_ids || []).filter((x) => x !== ev.id))} className="ml-1 text-magenta" data-testid={`checker-unassign-${r.id}`}>×</button>
                    </span>
                  ))}
                </div>
                <select className="input-dark !py-2 !text-xs mt-2" data-testid={`checker-assign-${r.id}`} value=""
                  onChange={(e) => e.target.value && setEvents(r.id, [...(r.allowed_event_ids || []), e.target.value])}>
                  <option value="">+ Allocate another event…</option>
                  {events.filter((ev) => !(r.allowed_event_ids || []).includes(ev.id)).map((ev) => (
                    <option key={ev.id} value={ev.id} className="bg-bg">{ev.title}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GalleryAdminTab({ onDone }) {
  const [form, setForm] = useState({ title: "", external_gallery_url: "", date: "", city: "Patna", category: "Event", banner: "" });
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const [pingEventId, setPingEventId] = useState("");
  const [pinging, setPinging] = useState(false);
  useEffect(() => { api.get("/events?limit=100").then(({ data }) => setEvents((data || []).filter((e) => e.ai_photo_gallery_url || e.external_gallery_url || e.ai_gallery_qr_path))).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/admin/gallery-events", form);
      toast.success(data.updated ? "Gallery updated" : "Gallery added — it now shows on the AI Gallery page");
      setForm({ title: "", external_gallery_url: "", date: "", city: "Patna", category: "Event", banner: "" });
      onDone?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const pingPhotos = async () => {
    if (!pingEventId) return toast.error("Pick an event first");
    setPinging(true);
    try {
      const { data } = await api.post("/admin/hooks/photos-ready", { event_id: pingEventId });
      toast.success(`Notified ${data.sent} fans`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Ping failed"); }
    finally { setPinging(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="admin-gallery-tab">
      <form onSubmit={submit} className="glass p-5 space-y-3">
        <div className="font-display text-3xl">Add an AI gallery</div>
        <p className="text-xs text-muted">Paste a new ai.eventom.online gallery link — it appears on the AI Photo Gallery page immediately (the public Refresh button re-reads this list).</p>
        <input required data-testid="gal-title" placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark" />
        <input required data-testid="gal-url" placeholder="https://ai.eventom.online/gallery/…?access_key=…" value={form.external_gallery_url} onChange={(e) => setForm({ ...form, external_gallery_url: e.target.value })} className="input-dark" />
        <div className="grid grid-cols-2 gap-3">
          <input type="date" required data-testid="gal-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-dark" />
          <input data-testid="gal-city" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-dark" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="gal-category" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-dark" />
          <input data-testid="gal-banner" placeholder="Cover image URL" value={form.banner} onChange={(e) => setForm({ ...form, banner: e.target.value })} className="input-dark" />
        </div>
        <button disabled={busy} className="btn-lime w-full justify-center" data-testid="gal-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Add to AI Gallery
        </button>
      </form>

      <div className="glass p-5 space-y-3">
        <div className="font-display text-3xl">Ping fans — photos ready</div>
        <p className="text-xs text-muted">Send in-app + email notifications to every ticket holder of a chosen event with an active AI gallery. Recommended after importing gallery photos.</p>
        <select value={pingEventId} onChange={(e) => setPingEventId(e.target.value)} data-testid="ping-photos-event" className="input-dark">
          <option value="" className="bg-bg">Choose an event with a gallery…</option>
          {events.map((ev) => <option key={ev.id} value={ev.id} className="bg-bg">{ev.title} · {ev.city || "—"}</option>)}
        </select>
        <button disabled={pinging || !pingEventId} onClick={pingPhotos} className="btn-primary w-full justify-center" data-testid="ping-photos-btn">
          {pinging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Notify ticket holders
        </button>
        <div className="text-[10px] font-mono text-muted">Each fan gets a bell notification + an email with a deep-link to open the AI Gallery.</div>
      </div>
    </div>
  );
}


function VipAdminTab() {
  const [apps, setApps] = useState([]);
  const [posts, setPosts] = useState([]);
  const load = () => {
    api.get("/admin/vip/applications").then(({ data }) => setApps(data || [])).catch(() => {});
    api.get("/admin/community/posts").then(({ data }) => setPosts(data || [])).catch(() => {});
  };
  useEffect(load, []);
  const decide = async (id, decision) => {
    try { await api.post(`/admin/vip/applications/${id}/decide`, { decision }); toast.success(`Application ${decision}`); load(); }
    catch { toast.error("Failed"); }
  };
  const removePost = async (id) => {
    try { await api.delete(`/community/posts/${id}`); toast.success("Post removed"); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div data-testid="admin-vip-tab">
    <OpportunitiesAdmin />
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="glass rounded-3xl p-5">
        <div className="font-display text-lg mb-3">VIP Applications <span className="text-muted text-sm font-body">({apps.length})</span></div>
        <div className="space-y-2 max-h-[520px] overflow-y-auto no-scrollbar">
          {apps.length === 0 && <div className="text-sm text-muted font-mono">No applications yet.</div>}
          {apps.map((a) => (
            <div key={a.id} className="rounded-2xl border border-white/10 bg-white/5 p-3" data-testid={`vip-app-${a.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{a.name || a.user_name || a.email}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted">{a.email} · {a.created_at?.slice(0, 10)}</div>
                </div>
                <span className={`px-2 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-widest ${a.status === "approved" ? "bg-lime text-ink" : a.status === "rejected" ? "bg-magenta text-white" : "bg-white/10 text-white/70"}`}>{a.status || "pending"}</span>
              </div>
              {a.reason && <div className="text-xs text-white/60 mt-1.5 line-clamp-2">{a.reason}</div>}
              {(a.status || "pending") === "pending" && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => decide(a.id, "approved")} data-testid={`vip-approve-${a.id}`} className="px-3 py-1 rounded-full bg-lime text-ink font-mono text-[10px] font-bold uppercase">Approve</button>
                  <button onClick={() => decide(a.id, "rejected")} data-testid={`vip-reject-${a.id}`} className="px-3 py-1 rounded-full bg-magenta text-white font-mono text-[10px] font-bold uppercase">Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="glass rounded-3xl p-5">
        <div className="font-display text-lg mb-3">Community Posts <span className="text-muted text-sm font-body">({posts.length})</span></div>
        <div className="space-y-2 max-h-[520px] overflow-y-auto no-scrollbar">
          {posts.length === 0 && <div className="text-sm text-muted font-mono">No posts yet.</div>}
          {posts.map((p) => (
            <div key={p.id} className={`rounded-2xl border p-3 ${Number(p.flag_count) > 0 ? "border-magenta/50 bg-magenta/10" : "border-white/10 bg-white/5"}`} data-testid={`admin-post-${p.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-purple">{p.user_name || p.user_id?.slice(0, 8)} · {p.created_at?.slice(0, 10)}</div>
                <div className="flex items-center gap-2 shrink-0">
                  {Number(p.flag_count) > 0 && <span className="px-2 py-0.5 rounded-full bg-magenta text-white font-mono text-[9px] font-bold uppercase">{p.flag_count} flags</span>}
                  <button onClick={() => removePost(p.id)} data-testid={`admin-delete-post-${p.id}`} className="px-2.5 py-1 rounded-full border border-magenta/60 text-magenta font-mono text-[9px] font-bold uppercase hover:bg-magenta hover:text-white transition-colors">Delete</button>
                </div>
              </div>
              <div className="text-sm text-white/80 mt-1.5">{p.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
    </div>
  );
}

function BlogAdminTab() {
  const blank = { title: "", slug: "", excerpt: "", body: "", cover_url: "", tags: "", meta_title: "", meta_description: "", author: "Eventom Team", published: true };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/blogs").then(({ data }) => setItems(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const pickCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Max 3MB");
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads?kind=blog-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, cover_url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}` });
      toast.success("Cover uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form, tags: (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean) };
      if (editing) { await api.patch(`/admin/blogs/${editing}`, payload); toast.success("Blog updated"); }
      else { await api.post("/admin/blogs", payload); toast.success("Blog published"); }
      setForm(blank); setEditing(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const edit = (b) => {
    setEditing(b.id);
    setForm({
      title: b.title || "", slug: b.slug || "", excerpt: b.excerpt || "", body: b.body || "",
      cover_url: b.cover_url || "", tags: (b.tags || []).join(", "),
      meta_title: b.meta_title || "", meta_description: b.meta_description || "",
      author: b.author || "Eventom Team", published: b.published !== false,
    });
    window.scrollTo({ top: 200, behavior: "smooth" });
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try { await api.delete(`/admin/blogs/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="admin-blog-tab">
      <form onSubmit={submit} className="glass p-5 space-y-3">
        <div className="font-display text-3xl">{editing ? "Edit blog" : "Write a blog"}</div>
        <p className="text-xs text-muted">SEO-oriented blog posts and news updates. Fill meta title & description for search engines.</p>
        <input required data-testid="blog-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark" />
        <input data-testid="blog-slug" placeholder="Slug (auto if blank) — e.g. how-to-host-a-college-fest" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input-dark !text-xs" />
        <textarea rows={2} data-testid="blog-excerpt" placeholder="Excerpt (shown on the list page)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="input-dark resize-none" />
        <textarea required rows={10} data-testid="blog-body" placeholder="Body — HTML allowed (paragraphs, <a>, <h2>, <img>, lists)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input-dark resize-y !text-sm font-mono" />
        <div className="flex items-center gap-3">
          <div className="w-20 h-14 rounded-xl bg-white/5 border border-white/15 overflow-hidden flex items-center justify-center shrink-0">
            {form.cover_url ? <img src={form.cover_url} alt="" className="w-full h-full object-cover" /> : <Upload className="w-4 h-4 text-muted" />}
          </div>
          <label className="btn-ghost !py-2 !px-3 !text-xs cursor-pointer" data-testid="blog-cover-upload"><Upload className="w-3 h-3" /> Upload cover<input type="file" accept="image/*" hidden onChange={pickCover} /></label>
          <input placeholder="…or paste URL" value={form.cover_url?.startsWith("http") ? form.cover_url : ""} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className="input-dark !text-xs" data-testid="blog-cover-url" />
        </div>
        <input data-testid="blog-tags" placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="input-dark !text-xs" />
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="blog-author" placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="input-dark !text-xs" />
          <label className="inline-flex items-center gap-2 text-xs text-muted"><input type="checkbox" data-testid="blog-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-lime" /> Published</label>
        </div>
        <input data-testid="blog-meta-title" placeholder="Meta title (SEO)" value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} className="input-dark !text-xs" />
        <textarea rows={2} data-testid="blog-meta-desc" placeholder="Meta description (SEO)" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} className="input-dark resize-none !text-xs" />
        <div className="flex gap-2">
          <button disabled={busy} className="btn-lime justify-center flex-1" data-testid="blog-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {editing ? "Save changes" : "Publish blog"}</button>
          {editing && <button type="button" onClick={() => { setEditing(""); setForm(blank); }} className="btn-ghost" data-testid="blog-cancel">Cancel</button>}
        </div>
      </form>

      <div className="glass p-5">
        <div className="font-display text-3xl mb-3">Blogs ({items.length})</div>
        {items.length === 0 ? <div className="text-sm text-muted font-mono">No blogs yet.</div> : (
          <div className="grid gap-3 max-h-[700px] overflow-y-auto no-scrollbar">
            {items.map((b) => (
              <div key={b.id} className="glass !rounded-xl p-3" data-testid={`blog-row-${b.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-sm truncate">{b.title}</div>
                    <div className="text-[10px] font-mono uppercase text-muted mt-0.5">/blog/{b.slug} · {b.created_at?.slice(0, 10)}</div>
                    {b.excerpt && <div className="text-xs text-white/60 mt-1 line-clamp-2">{b.excerpt}</div>}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full font-mono text-[9px] uppercase shrink-0 ${b.published ? "bg-lime text-ink" : "bg-white/10 text-white/70"}`}>{b.published ? "Live" : "Draft"}</span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <button onClick={() => edit(b)} data-testid={`blog-edit-${b.id}`} className="btn-ghost !py-1.5 !px-2.5 !text-[10px]"><Edit3 className="w-3 h-3" /> Edit</button>
                  <a href={`/blog/${b.slug}`} target="_blank" rel="noreferrer" data-testid={`blog-view-${b.id}`} className="btn-ghost !py-1.5 !px-2.5 !text-[10px]">Preview ↗</a>
                  <button onClick={() => remove(b.id)} data-testid={`blog-delete-${b.id}`} className="btn-ghost !py-1.5 !px-2.5 !text-[10px] !text-magenta"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Genie OCR trigger — one click, shows extracted name/DOB in a toast. */
function AadhaarOcrButton({ imageUrl }) {
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const run = async () => {
    if (!imageUrl) return toast.error("No Aadhaar image on this profile");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/clubvip/ocr-aadhaar", { image_url: imageUrl });
      setResult(data);
      const summary = [
        data.full_name ? `Name: ${data.full_name}` : null,
        data.dob ? `DOB: ${data.dob}` : null,
        typeof data.confidence === "number" ? `Confidence: ${(data.confidence * 100).toFixed(0)}%` : null,
      ].filter(Boolean).join(" · ");
      toast.success(summary || "OCR done — see JSON below");
    } catch (e) {
      toast.error(e.response?.data?.detail || "OCR failed");
    } finally { setLoading(false); }
  };
  return (
    <div className="inline-flex flex-col items-end gap-1">
      <button onClick={run} disabled={loading} data-testid="admin-ocr-aadhaar"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lime text-ink text-[9px] font-mono font-bold uppercase hover:scale-105 active:scale-95 transition-transform">
        <Sparkles className="w-3 h-3" /> {loading ? "…" : "Genie OCR"}
      </button>
      {result && (
        <div className="text-[9px] font-mono text-white/70 max-w-[180px] text-right leading-tight">
          {result.full_name && <div>{result.full_name}</div>}
          {result.dob && <div className="text-lime">{result.dob}</div>}
        </div>
      )}
    </div>
  );
}



function ClubVipAdminTab() {
  const [profiles, setProfiles] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get(`/admin/clubvip/profiles${filter ? `?status=${filter}` : ""}`).then(({ data }) => setProfiles(data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const decide = async (user_id, verification_status) => {
    try { await api.patch(`/admin/clubvip/profiles/${user_id}`, { verification_status });
      toast.success(verification_status === "approved" ? "Profile approved — now in deck" : "Rejected"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  return (
    <div data-testid="admin-clubvip-tab">
      <div className="flex flex-wrap gap-2 mb-4">
        {[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`clubvip-admin-filter-${k || "all"}`}
            className={`chip !text-[10px] ${filter === k ? "!bg-lime !text-ink !border-lime" : ""}`}>{l}</button>
        ))}
      </div>
      {profiles.length === 0 ? (
        <div className="glass p-8 text-center text-sm text-muted font-mono">No profiles in this bucket.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {profiles.map((p) => (
            <div key={p.user_id} className="glass rounded-3xl overflow-hidden" data-testid={`clubvip-admin-${p.user_id}`}>
              <div className="grid grid-cols-4 gap-1 p-2 pb-0">
                {(p.photos || []).slice(0, 3).map((ph, i) => (
                  <img key={i} src={ph} alt="" className="aspect-square rounded-lg object-cover" />
                ))}
                {p.live_selfie && (
                  <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-lime">
                    <img src={p.live_selfie} alt="live selfie" className="w-full h-full object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 bg-lime text-ink text-[9px] font-mono uppercase text-center py-0.5">LIVE</div>
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="font-heading font-bold">{p.name}<span className="text-muted font-body text-xs">, {p.age || "—"}</span></div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted">{p.city || "—"} · {p.gender || "—"}</div>
                {p.lifestyle_answers?.instagram_handle && (
                  <a href={`https://instagram.com/${p.lifestyle_answers.instagram_handle}`} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-magenta mt-1 inline-block hover:underline">@{p.lifestyle_answers.instagram_handle}</a>
                )}
                <div className="text-xs text-white/70 mt-2 line-clamp-2">{p.bio || "—"}</div>
                <div className="text-[10px] font-mono text-skyx mt-1.5">Vibe: {p.vibe || "—"}</div>
                <div className="mt-2 flex flex-wrap gap-1">{(p.interests || []).slice(0, 6).map((t) => <span key={t} className="chip !text-[9px]">{t}</span>)}</div>
                {/* KYC — Aadhaar previews. Only shown if the applicant uploaded them. */}
                {(p.lifestyle_answers?.aadhaar_front || p.lifestyle_answers?.aadhaar_back) && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-skyx">KYC · Aadhaar</div>
                      <AadhaarOcrButton imageUrl={p.lifestyle_answers.aadhaar_front || p.lifestyle_answers.aadhaar_back} />
                    </div>
                    <div className="grid grid-cols-2 gap-1.5" data-testid={`clubvip-kyc-preview-${p.user_id}`}>
                      {p.lifestyle_answers.aadhaar_front && (
                        <a href={p.lifestyle_answers.aadhaar_front} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-white/10 aspect-[16/10]">
                          <img src={p.lifestyle_answers.aadhaar_front} alt="aadhaar front" className="w-full h-full object-cover" />
                        </a>
                      )}
                      {p.lifestyle_answers.aadhaar_back && (
                        <a href={p.lifestyle_answers.aadhaar_back} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden border border-white/10 aspect-[16/10]">
                          <img src={p.lifestyle_answers.aadhaar_back} alt="aadhaar back" className="w-full h-full object-cover" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
                <div className="mt-3 flex gap-1.5 flex-wrap">
                  <span className={`chip !text-[9px] ${p.verification_status === "approved" ? "!bg-lime !text-ink !border-lime" : p.verification_status === "rejected" ? "!bg-magenta !text-white !border-magenta" : ""}`}>{p.verification_status || "pending"}</span>
                  {p.verification_status !== "approved" && <button onClick={() => decide(p.user_id, "approved")} data-testid={`clubvip-approve-${p.user_id}`} className="px-2.5 py-1 rounded-full bg-lime text-ink font-mono text-[9px] font-bold uppercase">Approve</button>}
                  {p.verification_status !== "rejected" && <button onClick={() => decide(p.user_id, "rejected")} data-testid={`clubvip-reject-${p.user_id}`} className="px-2.5 py-1 rounded-full bg-magenta text-white font-mono text-[9px] font-bold uppercase">Reject</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Club VIP Content admin (Feed / Contests / Opportunities CRUD) ──────────
const CVIP_KINDS = [
  { key: "feed",          label: "Feed",          endpoint: "feed",          blank: { title: "", body: "", image_url: "", link: "", pinned: false } },
  { key: "contests",      label: "Contests",      endpoint: "contests",      blank: { title: "", description: "", prize: "", image_url: "", ends_at: "", entry_link: "", status: "live" } },
  { key: "opportunities", label: "Opportunities", endpoint: "opportunities", blank: { title: "", description: "", kind: "after_party", image_url: "", date: "", city: "", request_link: "", price_hint: "", seats: null, status: "open" } },
];

function CvipContentAdminTab() {
  const [kind, setKind] = useState("feed");
  return (
    <div data-testid="admin-cvip-content-tab">
      <div className="flex gap-2 mb-4">
        {CVIP_KINDS.map((k) => (
          <button key={k.key} onClick={() => setKind(k.key)} data-testid={`cvip-content-sub-${k.key}`}
            className={`px-4 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border ${kind === k.key ? "bg-magenta text-white border-magenta" : "bg-white/5 text-muted border-white/10 hover:border-white/25"}`}>
            {k.label}
          </button>
        ))}
      </div>
      {CVIP_KINDS.map((k) => kind === k.key ? <CvipContentCrud key={k.key} kind={k} /> : null)}
    </div>
  );
}

// ─── Admin Offline QR — Generator + live check-in tracker (isolated) ───────
function OfflineQrAdminTab() {
  const [events, setEvents] = React.useState([]);
  const [eventId, setEventId] = React.useState("");
  const [tier, setTier] = React.useState("");
  const [qty, setQty] = React.useState(50);
  const [dayDate, setDayDate] = React.useState(""); // P2.2 multi-day
  const [busy, setBusy] = React.useState(false);
  const [lastBatch, setLastBatch] = React.useState(null);
  const [tracker, setTracker] = React.useState(null);
  const [filterTier, setFilterTier] = React.useState("");
  const [batches, setBatches] = React.useState([]);       // Iter 48
  const [confirm, setConfirm] = React.useState(null);     // {kind:"batch"|"row", data, scanned}
  const [exportBusy, setExportBusy] = React.useState("");
  const [selected, setSelected] = React.useState(new Set()); // Iter 49 — bulk select

  React.useEffect(() => {
    api.get("/admin/events").then(({ data }) => {
      const live = (data || []).filter((e) => (e.status || "").toLowerCase() === "approved" || (e.status || "").toLowerCase() === "live" || (e.status || "").toLowerCase() === "published");
      setEvents(live.length ? live : (data || []));
    }).catch(() => {});
  }, []);

  const selectedEvent = React.useMemo(() => events.find((e) => e.id === eventId), [events, eventId]);
  const tiers = React.useMemo(() => (selectedEvent?.tiers || []).map((t) => t.name || t.tier), [selectedEvent]);

  React.useEffect(() => { setTier(tiers[0] || ""); setDayDate(""); }, [tiers, eventId]);

  const loadTracker = React.useCallback(async () => {
    if (!eventId) return;
    try {
      const q = filterTier ? `?tier=${encodeURIComponent(filterTier)}` : "";
      const { data } = await api.get(`/admin/offline-qr/tracker/${eventId}${q}`);
      setTracker(data);
    } catch (err) { /* silent */ }
  }, [eventId, filterTier]);

  const loadBatches = React.useCallback(async () => {
    if (!eventId) { setBatches([]); return; }
    try {
      const { data } = await api.get(`/admin/offline-qr/batches/${eventId}`);
      setBatches(data?.batches || []);
    } catch { setBatches([]); }
  }, [eventId]);

  React.useEffect(() => { loadTracker(); loadBatches(); }, [loadTracker, loadBatches]);
  React.useEffect(() => {
    if (!eventId) return undefined;
    const t = setInterval(() => { loadTracker(); loadBatches(); }, 10000);
    return () => clearInterval(t);
  }, [eventId, loadTracker, loadBatches]);

  const generatePdf = async () => {
    if (!eventId || !tier) return toast.error("Pick event + tier first");
    if (qty < 1 || qty > 500) return toast.error("Quantity must be 1–500");
    const dayList = selectedEvent?.day_dates || [];
    const isMulti = (selectedEvent?.is_multi_day) || dayList.length > 1;
    if (isMulti && !dayDate) return toast.error("Pick a day for this multi-day event");
    setBusy(true);
    try {
      const token = localStorage.getItem("eventom_token");
      let url = `${API}/admin/offline-qr/generate.pdf?event_id=${encodeURIComponent(eventId)}&tier=${encodeURIComponent(tier)}&quantity=${qty}`;
      if (isMulti) url += `&day_date=${encodeURIComponent(dayDate)}`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `eventom-offline-qr-${(selectedEvent?.title || "event").replace(/\s+/g, "-").slice(0, 20)}-${tier.replace(/\s+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      setLastBatch({ count: qty, tier, event: selectedEvent?.title });
      toast.success(`${qty} passes generated and downloaded`);
      loadTracker(); loadBatches();
    } catch (err) {
      toast.error("Generation failed");
    } finally { setBusy(false); }
  };

  // Iter 48 — bulk exports
  const downloadBulk = async (kind) => {
    if (!eventId) return toast.error("Pick an event first");
    setExportBusy(kind);
    try {
      const token = localStorage.getItem("eventom_token");
      const url = kind === "pdf"
        ? `${API}/admin/offline-qr/export/${eventId}/combined.pdf`
        : `${API}/admin/offline-qr/export/${eventId}/batches.zip`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = kind === "pdf" ? `eventom-offline-qr-combined.pdf` : `eventom-offline-qr-batches.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(kind === "pdf" ? "Combined PDF downloaded" : "ZIP downloaded");
    } catch (err) {
      toast.error(kind === "pdf" ? "No active QRs to export" : "Export failed");
    } finally { setExportBusy(""); }
  };

  // Iter 48 — delete flows
  const deleteBatch = async (batch, force=false) => {
    try {
      const { data } = await api.post(`/admin/offline-qr/batches/${eventId}/${encodeURIComponent(batch.batch_id)}/delete`, { force });
      if (data?.refused) { setConfirm({ kind: "batch", data: batch, scanned: data.scanned_serials || [], refused: true, message: data.message }); return; }
      toast.success(`Deleted ${data.deleted}${data.voided ? ` · voided ${data.voided}` : ""}`);
      setConfirm(null); loadTracker(); loadBatches();
    } catch { toast.error("Delete failed"); }
  };

  const deleteRow = async (row) => {
    try {
      const { data } = await api.delete(`/admin/offline-qr/ticket/${row.id}`);
      toast.success(data?.voided ? `Voided ${row.serial_number}` : `Deleted ${row.serial_number}`);
      loadTracker(); loadBatches();
    } catch { toast.error("Delete failed"); }
  };

  // Iter 49 — bulk ops
  const toggleSel = (bid) => setSelected((prev) => { const n = new Set(prev); if (n.has(bid)) n.delete(bid); else n.add(bid); return n; });
  const clearSel = () => setSelected(new Set());
  const bulkDelete = async (force = false) => {
    try {
      const batch_ids = Array.from(selected);
      const { data } = await api.post(`/admin/offline-qr/batches/${eventId}/bulk-delete`, { batch_ids, force });
      if (data?.refused) {
        if (!window.confirm(`${data.refused_batches.length} batch(es) contain scanned QRs. Void them and delete the rest?`)) return;
        return bulkDelete(true);
      }
      toast.success(`Deleted ${data.deleted}${data.voided ? ` · voided ${data.voided}` : ""}`);
      clearSel(); loadTracker(); loadBatches();
    } catch { toast.error("Bulk delete failed"); }
  };
  const bulkExport = async (kind) => {
    setExportBusy(`bulk-${kind}`);
    try {
      const batch_ids = Array.from(selected);
      const token = localStorage.getItem("eventom_token");
      const url = kind === "pdf"
        ? `${API}/admin/offline-qr/batches/${eventId}/bulk-export`
        : `${API}/admin/offline-qr/batches/${eventId}/bulk-zip`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ batch_ids }),
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = kind === "pdf" ? "eventom-offline-qr-selected.pdf" : "eventom-offline-qr-selected.zip";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Export downloaded");
    } catch { toast.error("Bulk export failed"); }
    finally { setExportBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="admin-offline-qr-tab">
      {/* Compose card */}
      <div className="glass p-5 rounded-3xl">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="chip !text-lime !border-lime/40 mb-1">New feature</div>
            <div className="font-display text-3xl">Offline QR Ticket Generator</div>
            <div className="text-xs text-muted mt-1">Batch-print unique QR passes. Each one scans through the existing gate flow — double-scan is blocked automatically.</div>
          </div>
          {eventId && (
            <div className="flex gap-2">
              <button onClick={() => downloadBulk("pdf")} disabled={exportBusy === "pdf"} data-testid="offline-qr-export-pdf"
                className="rounded-2xl bg-lime text-ink font-fancy uppercase text-base px-4 py-3 shadow hover:shadow-lg transition-all disabled:opacity-50">
                <div className="text-striped leading-none" style={{ fontSize: 22 }}>ALL</div>
                <div className="text-[9px] font-mono tracking-widest mt-1">{exportBusy === "pdf" ? "Preparing…" : "Combined PDF"}</div>
              </button>
              <button onClick={() => downloadBulk("zip")} disabled={exportBusy === "zip"} data-testid="offline-qr-export-zip"
                className="rounded-2xl bg-pink text-white font-fancy uppercase text-base px-4 py-3 shadow hover:shadow-lg transition-all disabled:opacity-50">
                <div className="text-striped leading-none" style={{ fontSize: 22 }}>ZIP</div>
                <div className="text-[9px] font-mono tracking-widest mt-1">{exportBusy === "zip" ? "Zipping…" : "Per-batch zip"}</div>
              </button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-lime mb-1">1 · Event</label>
            <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="input-dark !text-xs" data-testid="offline-qr-event">
              <option value="">— pick event —</option>
              {events.map((e) => <option key={e.id} value={e.id}>{e.title} · {e.city || ""}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-lime mb-1">2 · Ticket tier</label>
            <select value={tier} onChange={(e) => setTier(e.target.value)} disabled={!eventId} className="input-dark !text-xs" data-testid="offline-qr-tier">
              {tiers.length === 0 && <option value="">— no tiers on event —</option>}
              {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {(selectedEvent?.day_dates?.length > 1) && (
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-pink mb-1">Day · multi-day event</label>
              <select value={dayDate} onChange={(e) => setDayDate(e.target.value)} className="input-dark !text-xs" data-testid="offline-qr-day">
                <option value="">— pick day —</option>
                {(selectedEvent?.day_dates || []).map((iso, i) => {
                  const d = new Date(iso);
                  return <option key={iso} value={iso}>D{i+1} · {d.toLocaleDateString("en-IN",{weekday:"short", day:"2-digit", month:"short"})}</option>;
                })}
              </select>
            </div>
          )}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-lime mb-1">3 · Quantity (max 500)</label>
            <input type="number" min={1} max={500} value={qty} onChange={(e) => setQty(parseInt(e.target.value || "0", 10))} className="input-dark !text-xs" data-testid="offline-qr-qty" />
          </div>
          <div className="flex items-end">
            <button onClick={generatePdf} disabled={busy || !eventId || !tier} className="btn-poster w-full justify-center !py-3 !px-4" data-testid="offline-qr-generate">
              {busy ? "…" : "Generate PDF →"}
            </button>
          </div>
        </div>

        {lastBatch && (
          <div className="mt-3 rounded-2xl border border-lime/40 bg-lime/5 p-3 text-[11px] font-mono text-lime" data-testid="offline-qr-last-batch">
            Last batch: {lastBatch.count} × {lastBatch.tier} for "{lastBatch.event}" — PDF downloaded.
          </div>
        )}
        <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-muted">
          Serial format: <span className="text-lime">EVE-TIER-###</span> (e.g. SUN-VIP-001) · One QR per A6 page · Sticker-print ready
        </div>
      </div>

      {/* Batches card — Iter 48 */}
      {eventId && batches.length > 0 && (
        <div className="glass p-5 rounded-3xl" data-testid="offline-qr-batches">
          <div className="mb-3">
            <div className="font-display text-2xl">Generated batches</div>
            <div className="text-xs text-muted mt-0.5">Each row = one Generate click. Delete the batch to retire all its unscanned QRs.</div>
          </div>
          <div className="space-y-2">
            {batches.map((b) => (
              <div key={b.batch_id} data-testid={`offline-qr-batch-${b.batch_id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                  <input type="checkbox" className="accent-lime w-4 h-4" checked={selected.has(b.batch_id)} onChange={() => toggleSel(b.batch_id)} data-testid={`offline-qr-batch-check-${b.batch_id}`} />
                  <span className="font-mono text-lime text-xs px-2 py-1 rounded-full bg-lime/10 border border-lime/40">{b.first_serial} → {b.last_serial}</span>
                  <span className="chip !text-[10px] !text-white !border-white/20">{b.tier}</span>
                  <span className="chip !text-[10px] !text-muted">{b.count} QRs</span>
                  {b.scanned > 0 && <span className="chip !text-[10px] !text-magenta !border-magenta/40">{b.scanned} scanned</span>}
                  <span className="text-[10px] font-mono text-muted">{(b.generated_at || "").replace("T", " ").slice(0, 16)}</span>
                </div>
                <button
                  data-testid={`offline-qr-delete-batch-${b.batch_id}`}
                  onClick={() => setConfirm({ kind: "batch", data: b })}
                  className="btn-pink !py-1.5 !px-3 !text-[10px]">
                  Delete batch →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tracker card */}
      {tracker && (
        <div className="glass p-5 rounded-3xl" data-testid="offline-qr-tracker">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <div className="font-display text-2xl">Live check-in tracker</div>
              <div className="text-xs text-muted mt-0.5">"{tracker.event.title}" — updates every 10s.</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="chip !text-white !border-white/20">Total: <b className="ml-1 text-lime">{tracker.total}</b></div>
              <div className="chip !text-white !border-white/20">Checked in: <b className="ml-1 text-lime">{tracker.checked_in}</b></div>
              <div className="chip !text-white !border-white/20">Pending: <b className="ml-1 text-magenta">{tracker.pending}</b></div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            <button onClick={() => setFilterTier("")} className={`chip ${!filterTier ? "!text-lime !border-lime/40" : "!text-muted"}`} data-testid="offline-qr-filter-all">All</button>
            {Object.entries(tracker.by_tier).map(([t, n]) => (
              <button key={t} onClick={() => setFilterTier(t)} className={`chip ${filterTier === t ? "!text-lime !border-lime/40" : "!text-muted"}`} data-testid={`offline-qr-filter-${t}`}>
                {t} <span className="ml-1 opacity-70">({n})</span>
              </button>
            ))}
          </div>
          <div className="max-h-[420px] overflow-y-auto rounded-2xl border border-white/10">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white/5 backdrop-blur">
                <tr className="text-[10px] font-mono uppercase tracking-widest text-muted">
                  <th className="text-left p-2">Serial</th>
                  <th className="text-left p-2">Tier</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Checked-in at</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tracker.entries.length === 0 && (
                  <tr><td colSpan={5} className="p-4 text-center text-muted font-mono">No passes yet. Generate a batch above.</td></tr>
                )}
                {tracker.entries.map((e) => (
                  <tr key={e.id} className={`border-t border-white/5 ${e.voided ? "opacity-50" : ""}`} data-testid={`offline-qr-row-${e.serial_number}`}>
                    <td className="p-2 font-mono text-lime">{e.serial_number}</td>
                    <td className="p-2">{e.tier}</td>
                    <td className="p-2">
                      {e.voided
                        ? <span className="chip !text-[9px] !text-orangex !border-orangex/40">⌀ Voided</span>
                        : e.checked_in
                        ? <span className="chip !text-[9px] !text-lime !border-lime/40">✓ Checked in</span>
                        : <span className="chip !text-[9px] !text-muted">Pending</span>}
                    </td>
                    <td className="p-2 text-muted font-mono">{e.checked_in_at ? e.checked_in_at.replace("T", " ").slice(0, 19) : "—"}</td>
                    <td className="p-2 text-right">
                      {!e.voided && (
                        <button
                          onClick={() => setConfirm({ kind: "row", data: e })}
                          data-testid={`offline-qr-delete-row-${e.serial_number}`}
                          className="icon-circle !w-7 !h-7 !bg-magenta/15 !text-magenta hover:!bg-magenta hover:!text-white transition-colors"
                          title={e.checked_in ? "Scanned — will be voided" : "Delete"}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {confirm && (
        <OfflineQrConfirmModal
          confirm={confirm}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            if (confirm.kind === "batch") deleteBatch(confirm.data, confirm.refused ? true : false);
            else if (confirm.kind === "row") { deleteRow(confirm.data); setConfirm(null); }
          }}
        />
      )}
      {/* Iter 49 — sticky bulk action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-5 right-5 z-40 rounded-full border-2 border-lime bg-black/90 backdrop-blur px-4 py-3 flex items-center gap-3 shadow-2xl" data-testid="offline-qr-bulk-bar">
          <span className="font-mono text-[10px] uppercase tracking-widest text-lime">{selected.size} selected</span>
          <button onClick={() => bulkDelete(false)} data-testid="offline-qr-bulk-delete" className="btn-pink !py-1.5 !px-3 !text-[10px]">Delete</button>
          <button onClick={() => bulkExport("pdf")} disabled={exportBusy === "bulk-pdf"} data-testid="offline-qr-bulk-pdf" className="btn-lime !py-1.5 !px-3 !text-[10px]">{exportBusy === "bulk-pdf" ? "…" : "Combined PDF"}</button>
          <button onClick={() => bulkExport("zip")} disabled={exportBusy === "bulk-zip"} data-testid="offline-qr-bulk-zip" className="btn-lime !py-1.5 !px-3 !text-[10px]">{exportBusy === "bulk-zip" ? "…" : "ZIP"}</button>
          <button onClick={clearSel} data-testid="offline-qr-bulk-clear" className="font-mono text-[10px] uppercase tracking-widest text-white/60 underline">Clear</button>
        </div>
      )}
    </div>
  );
}

function OfflineQrConfirmModal({ confirm, onCancel, onConfirm }) {
  const isBatch = confirm.kind === "batch";
  const title = isBatch
    ? (confirm.refused ? "Scanned QRs in this batch" : `Delete all ${confirm.data.count} QRs?`)
    : (confirm.data.checked_in ? "Already scanned — void it?" : `Delete ${confirm.data.serial_number}?`);
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onCancel} data-testid="offline-qr-confirm-modal">
      <div className="max-w-md w-full rounded-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="themed-pink sticker-wall p-5 relative">
          <div className="relative z-10">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/80">Confirm</div>
            <div className="font-fancy uppercase text-white leading-[0.9] text-3xl mt-1" style={{ textShadow: "2px 2px 0 rgba(0,0,0,0.3)" }}>
              {title}
            </div>
          </div>
        </div>
        <div className="bg-[#0A0A0F] p-5 space-y-3">
          {isBatch ? (
            <>
              <div className="text-sm text-white/80">
                {confirm.refused
                  ? confirm.message
                  : `Serial range ${confirm.data.first_serial} → ${confirm.data.last_serial}. Unscanned QRs will be deleted; scanned ones are protected unless you confirm again.`}
              </div>
              {confirm.refused && confirm.scanned?.length > 0 && (
                <div className="max-h-32 overflow-y-auto rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-[10px] font-mono text-orangex">
                  {confirm.scanned.join(" · ")}
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-white/80">
              {confirm.data.checked_in
                ? `${confirm.data.serial_number} was already scanned. It won't be deleted — it will be marked VOIDED so future scans are refused.`
                : `This removes ${confirm.data.serial_number} completely. No one can scan it after.`}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button onClick={onCancel} data-testid="offline-qr-confirm-cancel" className="btn-ghost !py-2 !px-4 !text-xs">Cancel</button>
            <button onClick={onConfirm} data-testid="offline-qr-confirm-confirm"
              className="btn-pink !py-2 !px-4 !text-xs">
              {isBatch
                ? (confirm.refused ? "Void scanned + delete rest" : "Delete batch")
                : (confirm.data.checked_in ? "Void it" : "Delete")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function CvipContentCrud({ kind }) {
  const [items, setItems] = useState([]);
  const [entries, setEntries] = useState({}); // contest_id → entrant count (contests only)
  const [form, setForm] = useState(kind.blank);
  const [editing, setEditing] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  const load = () => api.get(`/admin/clubvip/${kind.endpoint}`).then(async ({ data }) => {
    setItems(data || []);
    if (kind.key === "contests") {
      // Pull entry counts per contest
      const { data: allEntries } = await api.get("/admin/clubvip/contest-entries");
      const counts = {};
      (allEntries?.entries || []).forEach((e) => { counts[e.contest_id] = (counts[e.contest_id] || 0) + 1; });
      setEntries(counts);
    }
  }).catch(() => {});

  useEffect(() => { setForm(kind.blank); setEditing(""); load(); /* eslint-disable-next-line */ }, [kind.key]);

  const pickImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("Max 3MB");
    setImgBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/uploads?kind=cvip-${kind.key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, image_url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}` }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setImgBusy(false); e.target.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form };
      if (kind.key === "opportunities" && payload.seats === "") payload.seats = null;
      if (editing) { await api.patch(`/admin/clubvip/${kind.endpoint}/${editing}`, payload); toast.success("Saved"); }
      else { await api.post(`/admin/clubvip/${kind.endpoint}`, payload); toast.success("Created"); }
      setForm(kind.blank); setEditing(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const edit = (row) => {
    setEditing(row.id);
    const merged = { ...kind.blank, ...row };
    setForm(merged);
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete this ${kind.label.toLowerCase().slice(0, -1)}?`)) return;
    try { await api.delete(`/admin/clubvip/${kind.endpoint}/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const togglePublished = async (row) => {
    try { await api.patch(`/admin/clubvip/${kind.endpoint}/${row.id}`, { ...kind.blank, ...row, published: row.published === false ? true : false }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid={`cvip-content-crud-${kind.key}`}>
      {/* ── Left: form ─────────────────────────────── */}
      <form onSubmit={submit} className="glass p-5 space-y-3">
        <div className="font-display text-3xl">{editing ? `Edit ${kind.label.slice(0, -1)}` : `New ${kind.label.slice(0, -1)}`}</div>

        <input required placeholder="Title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark" data-testid={`cvip-${kind.key}-title`} />

        {kind.key === "feed" && (
          <textarea required rows={5} placeholder="Body (Markdown ok)" value={form.body || ""} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input-dark resize-y !text-sm" data-testid={`cvip-${kind.key}-body`} />
        )}
        {kind.key !== "feed" && (
          <textarea required rows={5} placeholder="Description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark resize-y !text-sm" data-testid={`cvip-${kind.key}-description`} />
        )}

        {kind.key === "contests" && (
          <>
            <input placeholder="Prize (e.g. ₹5000 gift card)" value={form.prize || ""} onChange={(e) => setForm({ ...form, prize: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-prize`} />
            <input placeholder="Ends at (ISO or human date)" value={form.ends_at || ""} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-ends`} />
            <input placeholder="Entry link (form/WhatsApp/IG)" value={form.entry_link || ""} onChange={(e) => setForm({ ...form, entry_link: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-entry-link`} />
            <select value={form.status || "live"} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-status`}>
              <option value="live">Live</option><option value="closed">Closed</option><option value="draft">Draft</option>
            </select>
          </>
        )}

        {kind.key === "opportunities" && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <select value={form.kind || "after_party"} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-kind`}>
                <option value="after_party">After party</option>
                <option value="house_party">House party</option>
                <option value="trip">Trip</option>
                <option value="luxury">Luxury</option>
                <option value="table">Table</option>
                <option value="other">Other</option>
              </select>
              <select value={form.status || "open"} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-status`}>
                <option value="open">Open</option><option value="full">Full</option><option value="closed">Closed</option><option value="draft">Draft</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-date`} />
              <input placeholder="City" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-city`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Price hint (e.g. ₹12K/head)" value={form.price_hint || ""} onChange={(e) => setForm({ ...form, price_hint: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-price`} />
              <input type="number" placeholder="Seats (optional)" value={form.seats ?? ""} onChange={(e) => setForm({ ...form, seats: e.target.value === "" ? null : parseInt(e.target.value, 10) })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-seats`} />
            </div>
            <input placeholder="Request access link (WhatsApp/DM/form)" value={form.request_link || ""} onChange={(e) => setForm({ ...form, request_link: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-request-link`} />
          </>
        )}

        {kind.key === "feed" && (
          <>
            <input placeholder="External link (optional)" value={form.link || ""} onChange={(e) => setForm({ ...form, link: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-link`} />
            <label className="inline-flex items-center gap-2 text-xs text-muted"><input type="checkbox" checked={form.pinned || false} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} className="accent-lime" data-testid={`cvip-${kind.key}-pinned`} /> Pinned to top</label>
          </>
        )}

        {/* Image row */}
        <div className="flex items-center gap-3">
          <div className="w-20 h-14 rounded-xl bg-white/5 border border-white/15 overflow-hidden flex items-center justify-center shrink-0">
            {form.image_url ? <img src={form.image_url} alt="" className="w-full h-full object-cover" /> : <Upload className="w-4 h-4 text-muted" />}
          </div>
          <label className="btn-ghost !py-2 !px-3 !text-xs cursor-pointer" data-testid={`cvip-${kind.key}-image-upload`}>
            <Upload className="w-3 h-3" /> {imgBusy ? "…" : "Upload image"}
            <input type="file" accept="image/*" hidden onChange={pickImage} />
          </label>
          <input placeholder="…or paste URL" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="input-dark !text-xs" data-testid={`cvip-${kind.key}-image-url`} />
        </div>

        <div className="flex gap-2">
          <button disabled={busy} className="btn-lime justify-center flex-1" data-testid={`cvip-${kind.key}-submit`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {editing ? "Save changes" : `Publish ${kind.label.slice(0, -1)}`}
          </button>
          {editing && <button type="button" onClick={() => { setEditing(""); setForm(kind.blank); }} className="btn-ghost" data-testid={`cvip-${kind.key}-cancel`}>Cancel</button>}
        </div>
      </form>

      {/* ── Right: list ─────────────────────────────── */}
      <div className="glass p-5">
        <div className="font-display text-3xl mb-3">{kind.label} ({items.length})</div>
        {items.length === 0 ? <div className="text-sm text-muted font-mono">Nothing here yet.</div> : (
          <div className="grid gap-3 max-h-[720px] overflow-y-auto no-scrollbar">
            {items.map((row) => (
              <div key={row.id} className="glass !rounded-xl p-3" data-testid={`cvip-${kind.key}-row-${row.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold text-sm truncate">{row.title}</div>
                    <div className="text-[10px] font-mono uppercase text-muted mt-0.5">
                      {row.created_at?.slice(0, 10)}
                      {kind.key === "contests" && row.status && <> · {row.status}</>}
                      {kind.key === "contests" && (entries[row.id] || 0) > 0 && <> · <span className="text-lime">{entries[row.id]} entries</span></>}
                      {kind.key === "opportunities" && <> · {row.kind}{row.city ? ` · ${row.city}` : ""} · {row.status}</>}
                      {kind.key === "feed" && row.pinned && <> · <span className="text-magenta">pinned</span></>}
                    </div>
                    {(row.body || row.description) && <div className="text-xs text-white/60 mt-1 line-clamp-2">{row.body || row.description}</div>}
                  </div>
                  {row.image_url && <img src={row.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <button onClick={() => edit(row)} data-testid={`cvip-${kind.key}-edit-${row.id}`} className="btn-ghost !py-1.5 !px-2.5 !text-[10px]"><Edit3 className="w-3 h-3" /> Edit</button>
                  {kind.key === "feed" && (
                    <button onClick={() => togglePublished(row)} data-testid={`cvip-${kind.key}-toggle-${row.id}`} className="btn-ghost !py-1.5 !px-2.5 !text-[10px]">
                      {row.published === false ? "Publish" : "Unpublish"}
                    </button>
                  )}
                  <button onClick={() => remove(row.id)} data-testid={`cvip-${kind.key}-delete-${row.id}`} className="btn-ghost !py-1.5 !px-2.5 !text-[10px] !text-magenta"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



// ─── Marketing (WhatsApp / SMS campaigns — stub provider) ───────────────────
function MarketingTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [f, setF] = useState({ name: "", channel: "whatsapp", template: "Hey {{name}}! Something for you tonight.", media_url: "", scheduled_for: "",
                               audience: { all_users: true, vip_only: false, city: "", event_id: "", days_since_attendance: null, custom_contacts: [] } });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);

  const load = () => api.get("/admin/marketing/campaigns").then(({ data }) => setCampaigns(data || [])).catch(() => {});
  useEffect(() => { load(); api.get("/admin/events").then(({ data }) => setEvents(data || [])).catch(() => {}); }, []);

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/campaigns/preview", f);
      setPreview(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Preview failed"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!f.name || !f.template) return toast.error("Name + template required");
    if (!window.confirm(`Send campaign "${f.name}"? Provider is currently in stub mode — no real messages leave the platform.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/campaigns", f);
      toast.success(`Campaign ${data.status}: ${data.sent || 0} sends, ${data.skipped_opted_out || 0} skipped`);
      setF({ ...f, name: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="admin-marketing-tab">
      <div className="glass p-5 space-y-3">
        <div className="font-display text-3xl">New campaign</div>
        <div className="text-xs text-muted">Stub provider: sends are logged only. Add MSG91/Twilio creds later — the pipeline is ready.</div>
        <input placeholder="Campaign name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-dark" data-testid="mkt-name" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} className="input-dark !text-xs" data-testid="mkt-channel">
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
            <option value="both">WhatsApp + SMS</option>
          </select>
          <input placeholder="Schedule (ISO, leave blank to send now)" value={f.scheduled_for} onChange={(e) => setF({ ...f, scheduled_for: e.target.value })} className="input-dark !text-xs" data-testid="mkt-scheduled" />
        </div>
        <textarea rows={5} placeholder="Message template — use {{name}} · {{city}}" value={f.template} onChange={(e) => setF({ ...f, template: e.target.value })} className="input-dark resize-y !text-sm" data-testid="mkt-template" />
        <input placeholder="Media URL (image/PDF, optional)" value={f.media_url} onChange={(e) => setF({ ...f, media_url: e.target.value })} className="input-dark !text-xs" data-testid="mkt-media" />

        <div className="rounded-2xl border border-white/10 p-3 space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Audience</div>
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.audience.vip_only} onChange={(e) => setF({ ...f, audience: { ...f.audience, vip_only: e.target.checked } })} className="accent-lime" data-testid="mkt-audience-vip" /> Club VIP only</label>
          <input placeholder="City filter (optional)" value={f.audience.city} onChange={(e) => setF({ ...f, audience: { ...f.audience, city: e.target.value } })} className="input-dark !text-xs" data-testid="mkt-audience-city" />
          <select value={f.audience.event_id} onChange={(e) => setF({ ...f, audience: { ...f.audience, event_id: e.target.value } })} className="input-dark !text-xs" data-testid="mkt-audience-event">
            <option value="">— any event —</option>
            {events.map((e) => <option key={e.id} value={e.id}>Attendees of: {e.title}</option>)}
          </select>
          <textarea rows={2} placeholder="Custom phone/email list (comma separated, optional)" value={(f.audience.custom_contacts || []).join(", ")} onChange={(e) => setF({ ...f, audience: { ...f.audience, custom_contacts: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } })} className="input-dark !text-xs resize-none" data-testid="mkt-audience-custom" />
        </div>

        <div className="flex gap-2">
          <button onClick={runPreview} disabled={busy} className="btn-ghost !py-2 !px-4" data-testid="mkt-preview">Preview</button>
          <button onClick={send} disabled={busy} className="btn-lime justify-center flex-1" data-testid="mkt-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send campaign
          </button>
        </div>

        {preview && (
          <div className="rounded-2xl border border-lime/40 bg-lime/5 p-3 mt-2" data-testid="mkt-preview-result">
            <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Audience: {preview.audience_size} · Provider: {preview.provider}</div>
            {preview.samples?.map((s, i) => (
              <div key={i} className="text-[11px] text-white/80 mt-1.5 border-t border-lime/20 pt-1.5">
                <div className="font-mono text-lime">→ {s.contact}</div>
                <div className="whitespace-pre-wrap">{s.rendered}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass p-5">
        <div className="font-display text-3xl mb-3">Campaigns ({campaigns.length})</div>
        {campaigns.length === 0 ? <div className="text-sm text-muted font-mono">No campaigns yet.</div> : (
          <div className="grid gap-2 max-h-[720px] overflow-y-auto no-scrollbar">
            {campaigns.map((c) => (
              <div key={c.id} className="glass !rounded-xl p-3" data-testid={`mkt-row-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-sm truncate">{c.name}</div>
                    <div className="text-[10px] font-mono uppercase text-muted mt-0.5">{c.channel} · {c.audience_size || 0} recipients · {c.created_at?.slice(0, 10)}</div>
                  </div>
                  <span className={`chip !text-[9px] ${c.status === "sent" ? "!text-lime !border-lime/40" : c.status === "scheduled" ? "!text-orangex !border-orangex/40" : ""}`}>{c.status}</span>
                </div>
                <div className="text-[11px] text-white/60 mt-1 line-clamp-2 whitespace-pre-wrap">{c.template}</div>
                {(c.delivered || c.skipped_opted_out) && (
                  <div className="text-[10px] font-mono text-muted mt-1">Delivered: {c.delivered || 0} · Skipped (opt-out): {c.skipped_opted_out || 0}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



function PushAdminTab() {
  const [events, setEvents] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [f, setF] = useState({ event_id: "", audience: "all", city: "", hint: "", title: "", body: "", link: "/", scheduled_for: "" });
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [result, setResult] = useState(null);
  const loadBroadcasts = () => api.get("/admin/notifications/broadcasts?limit=20").then(({ data }) => setBroadcasts(data || [])).catch(() => {});
  useEffect(() => { api.get("/admin/events").then(({ data }) => setEvents(data || [])).catch(() => {}); loadBroadcasts(); const t = setInterval(loadBroadcasts, 15000); return () => clearInterval(t); }, []);
  const draft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post("/admin/notifications/push-draft", {
        event_id: f.event_id || "", audience: f.audience, city: f.city || "", hint: f.hint || "",
      });
      setF((x) => ({ ...x, title: data.title, body: data.body, link: data.link }));
      toast.success("Genie drafted your push — review & broadcast");
    } catch (e) { toast.error(e.response?.data?.detail || "Draft failed"); }
    finally { setDrafting(false); }
  };
  const send = async () => {
    if (!f.title || !f.body) return toast.error("Title + body required");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/notifications/push-broadcast", {
        title: f.title, body: f.body, link: f.link || "/",
        audience: f.audience, city: f.city || "",
        scheduled_for: f.scheduled_for || "",
      });
      setResult(data);
      loadBroadcasts();
      if (data.scheduled) toast.success(`Scheduled for ${new Date(data.scheduled_for).toLocaleString("en-IN")}`);
      else toast.success(`Sent to ${data.audience_size} devices`);
    } catch (e) { toast.error(e.response?.data?.detail || "Broadcast failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="push-admin-tab">
      <div className="lg:col-span-2 space-y-3">
        <div className="glass rounded-3xl p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Push · Broadcast</div>
          <div className="font-display text-xl mt-1">Draft, then broadcast to every phone.</div>

          <div className="grid sm:grid-cols-3 gap-2 mt-4">
            <div className="sm:col-span-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Event (optional context)</label>
              <select value={f.event_id} onChange={(e) => setF({ ...f, event_id: e.target.value })} className="input-dark !mt-1" data-testid="push-event">
                <option value="" className="bg-bg">— None —</option>
                {events.slice(0, 60).map((e) => (
                  <option key={e.id} value={e.id} className="bg-bg">{e.title} · {e.city}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Audience</label>
              <select value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })} className="input-dark !mt-1" data-testid="push-audience">
                <option value="all" className="bg-bg">Everyone</option>
                <option value="vip" className="bg-bg">ClubVIP only</option>
                <option value="city" className="bg-bg">Specific city</option>
              </select>
            </div>
            {f.audience === "city" && (
              <div className="sm:col-span-3">
                <label className="text-[10px] font-mono uppercase tracking-widest text-muted">City</label>
                <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="e.g. Patna" className="input-dark !mt-1" data-testid="push-city" />
              </div>
            )}
            <div className="sm:col-span-3">
              <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Genie hint (optional angle)</label>
              <input value={f.hint} onChange={(e) => setF({ ...f, hint: e.target.value })} placeholder="e.g. flash 24h sale, tickets running out, Dandiya vibe" className="input-dark !mt-1" data-testid="push-hint" />
            </div>
          </div>

          <div className="mt-3 flex gap-2">
            <button onClick={draft} disabled={drafting} data-testid="push-draft-btn" className="btn-pink !py-2.5 !px-4 !text-xs inline-flex items-center gap-2">
              {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : "✨ Draft with Genie"}
            </button>
          </div>
        </div>

        <div className="glass rounded-3xl p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-magenta">The push (edit before sending)</div>
          <div className="grid gap-2 mt-3">
            <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title (≤48 chars)" maxLength={48} className="input-dark" data-testid="push-title" />
            <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Body (≤120 chars)" maxLength={120} rows={2} className="input-dark" data-testid="push-body" />
            <input value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} placeholder="Deep link (e.g. /events/xyz)" className="input-dark" data-testid="push-link" />
            <div className="grid grid-cols-2 gap-2 items-end">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Schedule (IST) — leave blank to send now</label>
                <input type="datetime-local" value={f.scheduled_for} onChange={(e) => setF({ ...f, scheduled_for: e.target.value })} className="input-dark !mt-1" data-testid="push-schedule" />
              </div>
              {f.scheduled_for && (
                <button type="button" onClick={() => setF({ ...f, scheduled_for: "" })} className="text-[10px] font-mono uppercase tracking-widest text-magenta underline pb-2">Clear schedule</button>
              )}
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <button onClick={send} disabled={busy || !f.title || !f.body} data-testid="push-send-btn" className="btn-lime !py-2.5 !px-4 !text-xs">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (f.scheduled_for ? "Schedule Broadcast →" : "Send Broadcast →")}
            </button>
            {result && (
              <div className="text-[11px] font-mono uppercase tracking-widest text-lime">
                {result.scheduled ? `Scheduled for ${new Date(result.scheduled_for).toLocaleString("en-IN")}` : `Sent to ${result.audience_size} device${result.audience_size === 1 ? "" : "s"} · FCM ${result.push_delivered} · In-app ${result.in_app_written}`}
              </div>
            )}
          </div>
        </div>

        {/* ANALYTICS — recent broadcasts */}
        <div className="glass rounded-3xl p-5" data-testid="push-analytics">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Analytics · Recent broadcasts</div>
              <div className="font-heading font-bold text-white mt-0.5">{broadcasts.length} in last 20</div>
            </div>
            <button onClick={loadBroadcasts} className="text-[10px] font-mono uppercase tracking-widest text-skyx">Refresh ↻</button>
          </div>
          {broadcasts.length === 0 ? (
            <div className="text-[11px] text-muted mt-3">Nothing sent yet.</div>
          ) : (
            <div className="mt-3 space-y-2">
              {broadcasts.map((b) => {
                const ctr = b.audience_size > 0 ? Math.round(((b.taps || 0) / b.audience_size) * 100) : 0;
                const opens = b.opens || 0;
                return (
                  <div key={b.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 flex flex-wrap items-center gap-3" data-testid={`push-broadcast-${b.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest font-bold ${b.status === "sent" ? "bg-lime/20 text-lime" : b.status === "scheduled" ? "bg-orangex/20 text-orangex" : "bg-white/10 text-white/60"}`}>{b.status}</span>
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted truncate">{b.audience}{b.city ? ` · ${b.city}` : ""} · {new Date(b.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                      <div className="font-heading font-bold text-white text-sm mt-0.5 line-clamp-1">{b.title}</div>
                      <div className="text-[11px] text-muted line-clamp-1">{b.body}</div>
                      {b.scheduled_for && b.status === "scheduled" && <div className="text-[10px] font-mono uppercase tracking-widest text-orangex mt-1">Fires {new Date(b.scheduled_for).toLocaleString("en-IN")}</div>}
                    </div>
                    <div className="flex gap-3 text-center shrink-0">
                      <div><div className="font-fancy text-lg text-lime leading-none">{b.audience_size || 0}</div><div className="text-[9px] font-mono uppercase tracking-widest text-muted">Reach</div></div>
                      <div><div className="font-fancy text-lg text-skyx leading-none">{opens}</div><div className="text-[9px] font-mono uppercase tracking-widest text-muted">Opens</div></div>
                      <div><div className="font-fancy text-lg text-magenta leading-none">{b.taps || 0}</div><div className="text-[9px] font-mono uppercase tracking-widest text-muted">Taps</div></div>
                      <div><div className="font-fancy text-lg text-orangex leading-none">{ctr}%</div><div className="text-[9px] font-mono uppercase tracking-widest text-muted">CTR</div></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* PREVIEW */}
      <div className="lg:col-span-1">
        <div className="glass rounded-3xl p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted">Live preview</div>
          <div className="mt-3 rounded-3xl bg-black/60 border border-white/10 p-3">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-lg bg-lime grid place-items-center text-ink font-black text-sm shrink-0">E</div>
              <div className="min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">EVENTOM · now</div>
                <div className="font-heading font-bold text-white text-sm leading-tight mt-0.5">{f.title || "Push title preview"}</div>
                <div className="text-[11px] text-white/70 leading-snug mt-0.5 line-clamp-3">{f.body || "Push body preview"}</div>
              </div>
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-3">Deep link: <span className="text-lime">{f.link || "/"}</span></div>
          <div className="text-[10px] font-mono text-muted mt-3 leading-relaxed">Needs <span className="text-lime">FCM_SERVER_KEY</span> in <code>backend/.env</code> to fan out. Even without it, the in-app bell notification is always delivered.</div>
        </div>
      </div>
    </div>
  );
}


function StoreAdminTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const load = () => api.get("/admin/store/products").then(({ data }) => setItems(data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(load, []);
  const toggle = async (p, key) => {
    try { await api.put(`/admin/store/products/${p.id}`, { ...p, [key]: !p[key] }); toast.success(`${key} → ${!p[key] ? "on" : "off"}`); load(); }
    catch (e) { toast.error("Update failed"); }
  };
  const remove = async (p) => { if (!window.confirm(`Delete "${p.title}"?`)) return; try { await api.delete(`/admin/store/products/${p.id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };
  return (
    <div data-testid="store-admin-tab">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Store · products</div>
          <div className="font-display text-xl">Merch & affiliate manager</div>
        </div>
        <button onClick={() => { setEditing(null); setShowAdd(true); }} data-testid="store-admin-add" className="btn-lime !py-2 !px-4 !text-xs">+ Add product</button>
      </div>
      {loading ? <div className="text-muted text-sm">Loading…</div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((p) => (
            <div key={p.id} data-testid={`store-admin-item-${p.id}`} className="glass rounded-3xl overflow-hidden">
              <div className="aspect-video overflow-hidden bg-black/40"><img src={p.image_url} alt="" className="w-full h-full object-cover" onError={(e) => e.currentTarget.style.opacity = 0.3} /></div>
              <div className="p-3">
                <div className="font-heading font-bold text-white text-sm leading-tight line-clamp-2">{p.title}</div>
                <div className="flex flex-wrap gap-1 mt-1 text-[9px] font-mono uppercase tracking-widest">
                  <span className="px-2 py-0.5 rounded-full bg-white/10 text-white">₹{p.price}</span>
                  <span className={`px-2 py-0.5 rounded-full ${p.category === "affiliate" ? "bg-orangex/20 text-orangex" : "bg-lime/20 text-lime"}`}>{p.category}</span>
                  {p.collection && <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/70">{p.collection}</span>}
                  {p.featured && <span className="px-2 py-0.5 rounded-full bg-magenta/20 text-magenta">Featured</span>}
                  {!p.active && <span className="px-2 py-0.5 rounded-full bg-black/50 text-white/60">Hidden</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                  <button onClick={() => toggle(p, "featured")} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 hover:bg-lime/20 hover:text-lime">{p.featured ? "Unfeature" : "Feature"}</button>
                  <button onClick={() => toggle(p, "active")} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 hover:bg-orangex/20 hover:text-orangex">{p.active ? "Hide" : "Show"}</button>
                  <button onClick={() => { setEditing(p); setShowAdd(true); }} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-skyx/20 text-skyx">Edit</button>
                  <button onClick={() => remove(p)} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full bg-magenta/20 text-magenta">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showAdd && <StoreProductModal editing={editing} onClose={() => { setShowAdd(false); setEditing(null); }} onSaved={() => { setShowAdd(false); setEditing(null); load(); }} />}
    </div>
  );
}

function StoreProductModal({ editing, onClose, onSaved }) {
  const [f, setF] = useState(editing || {
    title: "", description: "", image_url: "", price: 0, strike_price: null, currency: "INR",
    category: "merch", collection: "corporate", subcategory: "apparel", partner: "Eventom",
    affiliate_url: "", tags: [], stock: null, featured: false, active: true,
  });
  const [busy, setBusy] = useState(false);
  const [genieHint, setGenieHint] = useState("");
  const [genieBusy, setGenieBusy] = useState(false);
  const [teaser, setTeaser] = useState("");
  const askGenie = async () => {
    if (!genieHint) return toast.error("Give Genie a one-line hint");
    setGenieBusy(true); setTeaser("");
    try {
      const { data } = await api.post("/admin/store/genie-fill", { image_url: f.image_url || "", hint: genieHint, collection: f.collection });
      setF((x) => ({
        ...x,
        title: data.title || x.title,
        description: data.description || x.description,
        price: data.price || x.price,
        strike_price: data.strike_price ?? x.strike_price,
        subcategory: data.subcategory || x.subcategory,
      }));
      setTeaser(data.ig_teaser || "");
      toast.success("Genie filled it in — review & save");
    } catch (e) { toast.error(e.response?.data?.detail || "Genie fill failed"); }
    finally { setGenieBusy(false); }
  };
  const save = async () => {
    if (!f.title || !f.image_url) return toast.error("Title + Image URL required");
    setBusy(true);
    try {
      const payload = { ...f, tags: Array.isArray(f.tags) ? f.tags : String(f.tags || "").split(",").map(s => s.trim()).filter(Boolean) };
      if (editing?.id) await api.put(`/admin/store/products/${editing.id}`, payload);
      else await api.post("/admin/store/products", payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md p-4 overflow-y-auto" onClick={onClose} data-testid="store-admin-modal">
      <div className="max-w-2xl mx-auto mt-6 glass rounded-3xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-2xl">{editing ? "Edit product" : "Add product"}</div>
          <button onClick={onClose} className="icon-circle"><XCircle className="w-4 h-4" /></button>
        </div>

        {/* GENIE FILL — Merch Upload Studio */}
        <div className="rounded-2xl border border-magenta/40 bg-gradient-to-br from-magenta/15 to-lime/10 p-3 mb-4" data-testid="genie-merch-fill">
          <div className="text-[10px] font-mono uppercase tracking-widest text-magenta mb-2">Genie · Merch Upload Studio</div>
          <div className="flex gap-2">
            <input value={genieHint} onChange={(e) => setGenieHint(e.target.value)} placeholder="One-line hint (e.g. black heavyweight hoodie, lime embroidered logo)" className="input-dark flex-1 !py-2 !text-xs" data-testid="genie-hint-input" />
            <button type="button" onClick={askGenie} disabled={genieBusy} data-testid="genie-fill-btn" className="btn-pink !py-2 !px-3 !text-xs">
              {genieBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Ask Genie"}
            </button>
          </div>
          {teaser && (
            <div className="mt-2 flex items-center gap-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-lime shrink-0">IG teaser</div>
              <div className="text-[11px] text-white/85 line-clamp-2 flex-1">{teaser}</div>
              <button type="button" onClick={() => { navigator.clipboard.writeText(teaser); toast.success("Copied"); }} data-testid="genie-teaser-copy" className="text-[10px] font-mono uppercase tracking-widest text-lime shrink-0">Copy</button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Title *</label><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="input-dark !mt-1" /></div>
          <div className="col-span-2"><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Description</label><textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="input-dark !mt-1" /></div>
          <div className="col-span-2">
            <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Product image *</label>
            <div className="mt-1 flex gap-2 items-start">
              <label className="w-24 h-24 rounded-2xl border-2 border-dashed border-lime/50 hover:border-lime bg-black/40 grid place-items-center cursor-pointer overflow-hidden shrink-0" data-testid="store-image-picker">
                {f.image_url ? (
                  <img src={f.image_url.startsWith("http") ? f.image_url : `${process.env.REACT_APP_BACKEND_URL}${f.image_url}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center px-2"><Camera className="w-6 h-6 text-lime mx-auto" /><div className="text-[9px] font-mono uppercase tracking-widest text-white/70 mt-1">Drop photo</div></div>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  const fd = new FormData(); fd.append("file", file);
                  try {
                    const { data } = await api.post("/admin/store/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    const abs = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
                    setF((x) => ({ ...x, image_url: abs }));
                    toast.success("Photo uploaded");
                  } catch { toast.error("Upload failed"); }
                }} />
              </label>
              <input value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} className="input-dark flex-1" placeholder="…or paste a URL" />
            </div>
          </div>
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Price ₹ *</label><input type="number" value={f.price} onChange={(e) => setF({ ...f, price: Number(e.target.value) })} className="input-dark !mt-1" /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Strike Price ₹</label><input type="number" value={f.strike_price || ""} onChange={(e) => setF({ ...f, strike_price: e.target.value ? Number(e.target.value) : null })} className="input-dark !mt-1" /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Category</label>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="input-dark !mt-1">
              {["merch","affiliate","experiences","ticket"].map((c) => <option key={c} value={c} className="bg-bg">{c}</option>)}
            </select>
          </div>
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Collection</label>
            <select value={f.collection} onChange={(e) => setF({ ...f, collection: e.target.value })} className="input-dark !mt-1">
              {["corporate","genz","girls","college","concert","tech","lifestyle","collectible","affiliate"].map((c) => <option key={c} value={c} className="bg-bg">{c}</option>)}
            </select>
          </div>
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Subcategory</label>
            <select value={f.subcategory} onChange={(e) => setF({ ...f, subcategory: e.target.value })} className="input-dark !mt-1">
              {["apparel","accessories","bags","lifestyle","tech","collectibles","drinks","fashion"].map((c) => <option key={c} value={c} className="bg-bg">{c}</option>)}
            </select>
          </div>
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Partner</label><input value={f.partner} onChange={(e) => setF({ ...f, partner: e.target.value })} className="input-dark !mt-1" placeholder="Eventom / Amazon / Myntra" /></div>
          <div className="col-span-2"><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Affiliate URL (if any)</label><input value={f.affiliate_url || ""} onChange={(e) => setF({ ...f, affiliate_url: e.target.value })} className="input-dark !mt-1" placeholder="https://www.amazon.in/…" /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Stock</label><input type="number" value={f.stock || ""} onChange={(e) => setF({ ...f, stock: e.target.value ? Number(e.target.value) : null })} className="input-dark !mt-1" /></div>
          <div><label className="text-[10px] font-mono uppercase tracking-widest text-muted">Tags (comma)</label><input value={Array.isArray(f.tags) ? f.tags.join(", ") : (f.tags || "")} onChange={(e) => setF({ ...f, tags: e.target.value })} className="input-dark !mt-1" /></div>
          <label className="col-span-2 inline-flex items-center gap-2 text-xs"><input type="checkbox" checked={!!f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Featured on Store</label>
          <label className="col-span-2 inline-flex items-center gap-2 text-xs"><input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active (publicly listed)</label>
        </div>
        <div className="mt-6 flex gap-2">
          <button onClick={save} disabled={busy} className="btn-lime flex-1 justify-center">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [regs, setRegs] = useState([]);
  const [txs, setTxs] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showManualTicket, setShowManualTicket] = useState(false);
  const [showLeadImport, setShowLeadImport] = useState(false);
  const [userDetail, setUserDetail] = useState(null);

  const load = async () => {
    try {
      const results = await Promise.all([
        api.get("/admin/analytics"), api.get("/admin/events"), api.get("/admin/users"),
        api.get("/leads"), api.get("/admin/registrations"), api.get("/admin/transactions"),
        api.get("/admin/waitlist"),
      ]);
      const [a,e,u,l,r,t,w] = results.map(x => x.data);
      setAnalytics(a); setEvents(e); setUsers(u); setLeads(l); setRegs(r); setTxs(t); setWaitlist(w);
    } catch {}
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  // Deep-link support from ApprovalBell (URL ?tab= or admin-open-tab custom event)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("tab");
    if (t) setTab(t);
    const onEvent = (e) => { if (e.detail) setTab(e.detail); };
    window.addEventListener("admin-open-tab", onEvent);
    return () => window.removeEventListener("admin-open-tab", onEvent);
  }, []);

  if (loading) return <Layout><div className="p-10 font-display text-3xl uppercase">Loading...</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const approve = async (id) => { await api.patch(`/events/${id}`, { status: "approved" }); toast.success("Approved"); load(); };
  const reject = async (id) => { await api.patch(`/events/${id}`, { status: "rejected" }); toast("Rejected"); load(); };
  const feature = async (e) => { await api.patch(`/events/${e.id}`, { featured: !e.featured }); load(); };
  const trend = async (e) => { await api.patch(`/events/${e.id}`, { trending: !e.trending }); load(); };
  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event permanently?")) return;
    try { await api.delete(`/events/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed"); }
  };
  const deleteUser = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("User deleted"); setUserDetail(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed"); }
  };
  const deleteReg = async (id) => {
    if (!window.confirm("Delete this registration?")) return;
    try { await api.delete(`/admin/registrations/${id}`); toast.success("Registration removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed"); }
  };
  const updateLead = async (id, status) => { await api.patch(`/leads/${id}`, { status }); load(); };

  const exportCsv = async (kind) => {
    try {
      const token = localStorage.getItem("eventom_token");
      const res = await fetch(`${API}/admin/export/${kind}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `eventom-${kind}.csv`; a.click();
      URL.revokeObjectURL(url); toast.success(`Exported ${kind}`);
    } catch { toast.error("Export failed"); }
  };

  const kpis = [
    { label: "Total Events", value: analytics?.total_events ?? 0, color: "from-magenta/30 to-magenta/10" },
    { label: "Active", value: analytics?.active_events ?? 0, color: "from-lime/25 to-lime/5" },
    { label: "Pending", value: analytics?.pending_events ?? 0, color: "from-orangex/25 to-orangex/5" },
    { label: "Users", value: analytics?.total_users ?? 0, color: "from-skyx/25 to-skyx/5" },
    { label: "Organizers", value: analytics?.total_organizers ?? 0, color: "from-purplex/30 to-purplex/10" },
    { label: "Registrations", value: analytics?.total_registrations ?? 0, color: "from-white/10 to-white/0" },
    { label: "Tickets Sold", value: analytics?.tickets_sold ?? 0, color: "from-magenta/25 to-purplex/25" },
    { label: "Revenue (₹)", value: (analytics?.total_revenue ?? 0).toLocaleString("en-IN"), color: "from-lime/20 to-skyx/20" },
    { label: "Online Tickets", value: analytics?.online_tickets ?? 0, color: "from-skyx/25 to-lime/10" },
    { label: "Cash Tickets", value: analytics?.cash_tickets ?? 0, color: "from-orangex/25 to-magenta/10" },
    { label: "Pass Tickets", value: analytics?.pass_tickets ?? 0, color: "from-lime/25 to-lime/5" },
    { label: "Brand Leads", value: analytics?.brand_leads ?? 0, color: "from-magenta/30 to-magenta/10" },
    { label: "Organiser Leads", value: analytics?.organizer_leads ?? 0, color: "from-lime/25 to-lime/5" },
    { label: "Service Leads", value: analytics?.service_leads ?? 0, color: "from-purplex/25 to-purplex/5" },
    { label: "Total Views", value: analytics?.website_visits ?? 0, color: "from-skyx/25 to-skyx/5" },
    { label: "Total Leads", value: leads.length, color: "from-magenta/25 to-orangex/25" },
  ];

  return (
    <Layout>
      <div className="border-b border-white/5 bg-gradient-to-r from-magenta/15 to-purplex/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="font-mono text-[10px] uppercase tracking-widest text-lime">Super Admin</div>
          <h1 className="font-display text-4xl md:text-6xl uppercase leading-[0.9]">Eventom <span className="grad-text">control room.</span></h1>
          <a href="/scanner" data-testid="admin-scanner-link" className="mt-4 inline-flex items-center gap-2 btn-lime !py-2 !px-4"><TicketIcon className="w-4 h-4" /> Scan Tickets →</a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {["overview","events","users","registrations","tickets","offlineqr","gatestaff","gallery","vip","clubvip","content","contests","blog","partners","store","genie","push","coupons","campaigns","marketing","kyc","payouts","leads","waitlist","homestrips","export"].map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
              className={`px-3 py-2 text-xs font-mono uppercase tracking-widest rounded-full ${tab === t ? "bg-lime text-bg" : "bg-white/5 text-muted"}`}>{t}</button>
          ))}
          <button onClick={load} data-testid="admin-refresh" className="px-3 py-2 text-xs font-mono uppercase tracking-widest rounded-full bg-magenta text-white ml-auto">Refresh ↻</button>
        </div>

        {tab === "coupons" && <CouponsTab />}
        {tab === "offlineqr" && <OfflineQrAdminTab />}
        {tab === "gatestaff" && <GateStaffTab events={events} />}
        {tab === "contests" && <ContestsAdminTab />}
        {tab === "blog" && <BlogAdminTab />}
        {tab === "clubvip" && <ClubVipAdminTab />}
        {tab === "content" && <CvipContentAdminTab />}
        {tab === "gallery" && <GalleryAdminTab onDone={load} />}
        {tab === "vip" && <VipAdminTab />}
        {tab === "partners" && <PartnersAdminTab />}
        {tab === "store" && <StoreAdminTab />}
        {tab === "push" && <PushAdminTab />}
        {tab === "genie" && <GenieAnalyticsTab />}
        {tab === "campaigns" && <CampaignsTab />}
        {tab === "marketing" && <MarketingTab />}
        {tab === "kyc" && <KycTab />}
        {tab === "payouts" && <PayoutsTab />}
        {tab === "homestrips" && <HomeStripsAdminTab />}

        {tab === "overview" && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {kpis.map((k) => (
              <div key={k.label} className={`bg-gradient-to-br ${k.color} border border-white/10 rounded-3xl p-5`} data-testid={`kpi-${k.label.toLowerCase().replace(/[()₹]/g,'').trim().replace(/\s+/g,'-')}`}>
                <div className="font-display text-4xl leading-none grad-text-hot">{k.value}</div>
                <div className="text-xs font-mono uppercase tracking-widest mt-2 text-muted">{k.label}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "events" && (
          <div>
            <div className="mb-4"><AddEventCard onDone={load} /></div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((e) => (
                <div key={e.id} className="glass rounded-3xl overflow-hidden" data-testid={`admin-event-${e.id}`}>
                  {e.banner && <div className="aspect-video overflow-hidden"><img src={e.banner} alt="" className="w-full h-full object-cover" /></div>}
                  <div className="p-4">
                    <div className="font-display text-lg uppercase leading-tight">{e.title}</div>
                    <div className="text-xs font-mono uppercase text-muted mt-1">{e.city} • {new Date(e.date).toLocaleDateString()}</div>
                    <div className="flex flex-wrap gap-1 mt-2 text-[10px] font-mono uppercase tracking-widest">
                      <span className={`px-2 py-0.5 rounded-full ${e.status === "approved" ? "bg-lime text-bg" : e.status === "pending" ? "bg-orangex text-white" : "bg-magenta text-white"}`}>{e.status}</span>
                      {(e.event_type || "ticketed") !== "ticketed" && <span className="px-2 py-0.5 rounded-full bg-skyx/20 text-skyx">{(e.event_type || "").replace("_", " ")}</span>}
                      {e.featured && <span className="px-2 py-0.5 rounded-full bg-lime/20 text-lime">Featured</span>}
                      {e.trending && <span className="px-2 py-0.5 rounded-full bg-magenta/20 text-magenta">Trending</span>}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {e.status !== "approved" && <button onClick={() => approve(e.id)} data-testid={`approve-${e.id}`} className="text-xs px-3 py-1.5 bg-lime text-bg rounded-full font-bold uppercase"><CheckCircle2 className="w-3 h-3 inline mr-1" />Approve</button>}
                      {e.status !== "rejected" && <button onClick={() => reject(e.id)} data-testid={`reject-${e.id}`} className="text-xs px-3 py-1.5 bg-white/5 text-white rounded-full font-bold uppercase"><XCircle className="w-3 h-3 inline mr-1" />Reject</button>}
                      <button onClick={() => feature(e)} className="text-xs px-3 py-1.5 bg-white/5 rounded-full font-bold uppercase">{e.featured ? "Unfeature" : "Feature"}</button>
                      <button onClick={() => trend(e)} className="text-xs px-3 py-1.5 bg-white/5 rounded-full font-bold uppercase">{e.trending ? "Untrend" : "Trending"}</button>
                      <button onClick={() => setEditing(e)} data-testid={`admin-edit-${e.id}`} className="text-xs px-3 py-1.5 bg-skyx/20 text-skyx rounded-full font-bold uppercase"><Edit3 className="w-3 h-3 inline mr-1" />Edit</button>
                      <button onClick={() => deleteEvent(e.id)} data-testid={`admin-delete-${e.id}`} className="text-xs px-3 py-1.5 bg-magenta/20 text-magenta rounded-full font-bold uppercase"><Trash2 className="w-3 h-3 inline mr-1" />Delete</button>
                      <ImportEventUsersButton eventId={e.id} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "users" && (
          <div>
            <UserImportInline onDone={load} />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {users.map((u) => (
                <div key={u.id} className="glass rounded-2xl p-4 flex items-center gap-3" data-testid={`admin-user-${u.id}`}>
                  {u.profile_picture ? <img src={u.profile_picture} alt="" className="w-14 h-14 rounded-2xl object-cover flex-shrink-0" /> : <div className="w-14 h-14 rounded-2xl bg-pink text-white flex-shrink-0 flex items-center justify-center font-display text-xl">{u.name?.[0]}</div>}
                  <div className="min-w-0 flex-1">
                    <div className="font-bold uppercase text-sm truncate">{u.name}</div>
                    <div className="text-xs text-muted truncate">{u.email}</div>
                    <div className="text-[10px] font-mono text-muted mt-0.5">{u.phone || "—"}</div>
                    <div className="flex items-center gap-1 mt-1">
                      <span className={`px-2 py-0.5 rounded-full font-mono text-[9px] uppercase ${u.role === "admin" ? "bg-pink text-white" : u.role === "organizer" ? "bg-lime text-ink" : "bg-white/10"}`}>{u.role}</span>
                      <span className="text-[9px] font-mono text-muted">{new Date(u.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setUserDetail(u)} data-testid={`user-view-${u.id}`} className="text-[10px] font-mono uppercase px-2 py-1 bg-white/5 rounded-full">View</button>
                    {u.role !== "admin" && <button onClick={() => { if (window.confirm(`Delete ${u.name}?`)) deleteUser(u.id); }} data-testid={`user-delete-${u.id}`} className="text-[10px] font-mono uppercase px-2 py-1 bg-magenta/20 text-magenta rounded-full"><Trash2 className="w-3 h-3 inline" /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "registrations" && (
          <div className="glass rounded-3xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-left font-mono text-xs uppercase tracking-widest">
                <tr><th className="p-3">Attendee</th><th className="p-3">Event</th><th className="p-3">Contact</th><th className="p-3">Source</th><th className="p-3">Date</th><th className="p-3">Actions</th></tr>
              </thead>
              <tbody>
                {regs.map((r) => {
                  const u = users.find((x) => x.id === r.user_id);
                  return (
                    <tr key={r.id} className="border-b border-white/5" data-testid={`admin-reg-${r.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {u?.profile_picture ? <img src={u.profile_picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-lime text-ink flex items-center justify-center font-display text-sm">{(r.user_name || "?")[0]}</div>}
                          <div><div className="font-bold">{r.user_name}</div><div className="text-[10px] font-mono text-muted">{u?.role || "guest"}</div></div>
                        </div>
                      </td>
                      <td className="p-3 font-bold text-xs">{r.event_title}</td>
                      <td className="p-3 text-muted text-xs">{r.user_email}<br /><span className="text-[10px]">{r.user_phone || "—"}</span></td>
                      <td className="p-3"><span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/5 uppercase">{r.source || "web"}</span></td>
                      <td className="p-3 font-mono text-xs text-muted">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="p-3"><button onClick={() => deleteReg(r.id)} data-testid={`reg-delete-${r.id}`} className="text-[10px] font-mono uppercase px-2 py-1 bg-magenta/20 text-magenta rounded-full"><Trash2 className="w-3 h-3 inline" /></button></td>
                    </tr>
                  );
                })}
                {regs.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted font-mono text-sm">No registrations yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "tickets" && (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <button onClick={() => setShowManualTicket(true)} data-testid="open-manual-ticket" className="btn-primary !py-2 !px-4"><TicketIcon className="w-4 h-4" /> Generate Ticket (Pass / Cash)</button>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted">Issue a ticket manually for an attendee without going through Razorpay.</div>
            </div>
            <div className="glass rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-left font-mono text-xs uppercase tracking-widest">
                  <tr><th className="p-3">Event</th><th className="p-3">Attendee</th><th className="p-3">Tier</th><th className="p-3">Qty</th><th className="p-3">Amount</th><th className="p-3">Method</th><th className="p-3">Status</th><th className="p-3">Date</th></tr>
                </thead>
                <tbody>
                  {txs.map((t) => {
                    const method = t.payment_method || (t.provider === "manual" ? "manual" : "online");
                    const badgeColor = method === "pass" ? "bg-lime text-bg" : method === "cash" ? "bg-orangex text-white" : "bg-skyx text-bg";
                    return (
                      <tr key={t.session_id} className="border-b border-white/5" data-testid={`admin-tx-${t.session_id}`}>
                        <td className="p-3 font-bold">{t.event_title}</td>
                        <td className="p-3">{t.attendee_name || t.user_email || "—"}<div className="text-[10px] font-mono text-muted">{t.attendee_phone || ""}</div></td>
                        <td className="p-3">{t.tier}</td>
                        <td className="p-3">{t.quantity}</td>
                        <td className="p-3">₹{t.amount}</td>
                        <td className="p-3"><span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase ${badgeColor}`}>{method}</span></td>
                        <td className="p-3"><span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase ${t.payment_status === "paid" ? "bg-lime text-bg" : t.payment_status === "pending" ? "bg-orangex text-white" : "bg-magenta text-white"}`}>{t.payment_status}</span></td>
                        <td className="p-3 font-mono text-xs text-muted">{new Date(t.created_at).toLocaleDateString()}</td>
                      </tr>
                    );
                  })}
                  {txs.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted font-mono text-sm">No tickets yet. Generate one manually to get started.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <button onClick={() => setShowLeadImport(true)} data-testid="open-lead-import" className="btn-lime !py-2 !px-4"><FileSpreadsheet className="w-4 h-4" /> Import Leads (Excel / CSV)</button>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted">Bulk import for Organisers · Colleges · Attendees · Artists · Sponsors</div>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-lime mb-2">Lead CRM 6 stages</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {LEAD_STATUSES.map((status) => {
                const lst = leads.filter((l) => l.status === status);
                return (
                  <div key={status} className="min-h-[300px] glass rounded-3xl overflow-hidden" data-testid={`lead-col-${status.replace(/\s+/g,'-').toLowerCase()}`}>
                    <div className={`${STATUS_COLORS[status]} px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-center`}>{status} ({lst.length})</div>
                    <div className="p-2 space-y-2">
                      {lst.map((l) => (
                        <div key={l.id} className="rounded-xl border border-white/10 p-2 bg-white/5 text-xs" data-testid={`lead-card-${l.id}`}>
                          <div className="font-bold uppercase">{l.name}</div>
                          <div className="font-mono text-[10px] text-muted">{l.phone}</div>
                          <div className="mt-1 text-[10px] font-mono uppercase text-magenta">{l.service || l.type}</div>
                          {l.company && <div className="text-[10px] font-mono text-muted">{l.company}</div>}
                          <select value={l.status} onChange={(ev) => updateLead(l.id, ev.target.value)} className="mt-2 w-full text-[10px] bg-white/5 border border-white/10 px-1 py-1 rounded font-mono uppercase">
                            {LEAD_STATUSES.map((s) => <option key={s} className="bg-bg">{s}</option>)}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "waitlist" && (
          <div className="glass rounded-3xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-left font-mono text-xs uppercase tracking-widest">
                <tr><th className="p-3">Email</th><th className="p-3">Module</th><th className="p-3">Date</th></tr>
              </thead>
              <tbody>
                {waitlist.map((w) => (
                  <tr key={w.id} className="border-b border-white/5" data-testid={`wl-${w.id}`}>
                    <td className="p-3 font-bold">{w.email}</td>
                    <td className="p-3"><span className="px-2 py-0.5 bg-lime/20 text-lime rounded-full font-mono text-[10px] uppercase">{w.module}</span></td>
                    <td className="p-3 font-mono text-xs text-muted">{new Date(w.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {waitlist.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-muted font-mono text-sm">No waitlist entries yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "export" && (
          <>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {EXPORTS.map((k) => (
              <button key={k} onClick={() => exportCsv(k)} data-testid={`export-${k}`} className="glass rounded-3xl p-6 text-left card-lift">
                <Download className="w-6 h-6 text-lime" />
                <div className="font-display text-2xl uppercase mt-2">{k}</div>
                <div className="text-xs font-mono uppercase text-muted mt-1">Download CSV</div>
              </button>
            ))}
          </div>
          <FreshStartDangerZone />
          </>
        )}

        {editing && <EditEventModal event={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        {showManualTicket && <ManualTicketModal events={events.filter(e => e.status === "approved")} onClose={() => setShowManualTicket(false)} onSaved={() => { setShowManualTicket(false); load(); }} />}
        {showLeadImport && <LeadImportModal onClose={() => setShowLeadImport(false)} onDone={() => { setShowLeadImport(false); load(); }} />}
        {userDetail && <UserDetailModal user={userDetail} onClose={() => setUserDetail(null)} onDelete={deleteUser} />}
      </div>
    </Layout>
  );
}

// Iter 47 — Admin editor for the 3 Home themed poster strips
function HomeStripsAdminTab() {
  const [strips, setStrips] = useState({ takeover: null, rizz: null, disco: null });
  const [saving, setSaving] = useState("");
  const load = async () => {
    try {
      const { data } = await api.get("/admin/themed-strips");
      const map = { takeover: null, rizz: null, disco: null };
      (data.strips || []).forEach((s) => { map[s.slot] = s; });
      setStrips(map);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load strips"); }
  };
  useEffect(() => { load(); }, []);

  const DEFAULTS = {
    takeover: { slot: "takeover", theme: "pink",  headline: "Girls", tail: "Takeover.", active: true },
    rizz:     { slot: "rizz",     theme: "blue",  headline: "48",    tail: "Hours.",    active: true },
    disco:    { slot: "disco",    theme: "yellow",headline: "Disco", tail: "Fever.",    active: true },
  };

  const save = async (slot) => {
    setSaving(slot);
    const payload = { ...(strips[slot] || DEFAULTS[slot]), slot };
    try {
      await api.post("/admin/themed-strips", payload);
      toast.success(`${slot} strip saved`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(""); }
  };

  const toggle = async (slot) => {
    const cur = strips[slot];
    if (!cur) return save(slot);
    try {
      await api.patch(`/admin/themed-strips/${slot}`, { active: !cur.active });
      toast.success(`${slot} ${!cur.active ? "live" : "hidden"}`);
      load();
    } catch (e) { toast.error("Could not toggle"); }
  };

  const setField = (slot, field, value) => {
    setStrips((prev) => ({ ...prev, [slot]: { ...(prev[slot] || DEFAULTS[slot]), slot, [field]: value } }));
  };

  const SLOTS = [
    { key: "takeover", title: "Pink · Girls Takeover" },
    { key: "rizz",     title: "Blue · 48 Hours Rizz" },
    { key: "disco",    title: "Yellow · Disco Fever" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-homestrips-tab">
      <div className="rounded-3xl border border-lime/30 bg-lime/5 p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-lime">Home themed strips</div>
        <div className="text-sm text-white/80 mt-1">Edit the 3 colour-themed poster strips that run on the homepage (Girls Takeover · 48 HOURS Rizz · Disco Fever). Changes go live instantly for every visitor.</div>
      </div>

      {SLOTS.map(({ key, title }) => {
        const s = strips[key] || DEFAULTS[key];
        return (
          <div key={key} className="rounded-3xl border border-white/10 bg-white/[0.03] p-5" data-testid={`admin-strip-${key}`}>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div>
                <div className={`text-[10px] font-mono uppercase tracking-widest ${s.theme === "pink" ? "text-pink" : s.theme === "yellow" ? "text-[#FFC600]" : s.theme === "blue" ? "text-skyx" : "text-purple"}`}>{title}</div>
                <div className="font-display text-lg text-white">Slot: <span className="font-mono">{key}</span></div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggle(key)} data-testid={`strip-toggle-${key}`}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest ${s.active ? "bg-lime text-ink" : "bg-white/10 text-white/70"}`}>
                  {s.active ? "Live" : "Hidden"}
                </button>
                <button onClick={() => save(key)} disabled={saving === key} data-testid={`strip-save-${key}`}
                  className="btn-primary !py-1.5 !px-4 !text-xs">
                  {saving === key ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-white/60">Theme</label>
                <select value={s.theme || "pink"} onChange={(e) => setField(key, "theme", e.target.value)}
                  className="input-dark !mt-1" data-testid={`strip-theme-${key}`}>
                  {["pink","yellow","blue","purple"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {[
                ["eyebrow", "Eyebrow (small kicker)"],
                ["headline", "Headline (big word)"],
                ["tail", "Tail (striped word, ends with period)"],
                ["meta_line", "Meta line (date · venue)"],
                ["cta_label", "CTA label"],
                ["cta_href", "CTA link"],
                ["secondary_label", "Secondary label"],
                ["secondary_href", "Secondary link"],
                ["image_url", "Image URL (/assets/… or https://)"],
                ["sticker_badge", "Sticker badge (big digits)"],
                ["sticker_sub", "Sticker sub (small text)"],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="text-[10px] font-mono uppercase tracking-widest text-white/60">{label}</label>
                  <input value={s[k] || ""} onChange={(e) => setField(key, k, e.target.value)} className="input-dark !mt-1" data-testid={`strip-${key}-${k}`} />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="text-[10px] font-mono uppercase tracking-widest text-white/60">Subcopy</label>
                <textarea value={s.subcopy || ""} onChange={(e) => setField(key, "subcopy", e.target.value)} rows={3} className="input-dark !mt-1" data-testid={`strip-${key}-subcopy`} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

